/**
 * ContractsEnhanced — Phase 05 redesign
 * Mail-client split: contract list (left) + detail panel (right)
 * Templates tab: full CRUD for contract templates
 */
import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Icons,
  KPI,
  EmptyState, QueryErrorBanner,
  Drawer, DrawerBody, DrawerFooter,
  CurrencyInput,
} from '../shared/ui';
import api from '../utils/api';
import { contractService } from '../services/apiService';
import ServiceConfigManager from '../components/admin/ServiceConfigManager';

const intlLocale = (lang) => (lang === 'en' ? 'en-US' : 'vi-VN');

const VND = (n, lang = 'vi') =>
  new Intl.NumberFormat(intlLocale(lang), { style: 'currency', currency: 'VND' }).format(n ?? 0);

const fmtDate = (d, lang = 'vi') =>
  d ? new Date(d).toLocaleDateString(intlLocale(lang), { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

// ── file download helpers ─────────────────────────────────────────────────────
const safeFilePart = (s) => String(s ?? '').replace(/[^\w.-]+/g, '-').replace(/^-+|-+$/g, '') || 'document';

function saveBlob(data, filename, mime) {
  const url = window.URL.createObjectURL(new Blob([data], { type: mime }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// Blob responses carry their error body as a Blob, so the usual err.response.data.detail is undefined.
async function blobErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text())?.detail || fallback;
    } catch {
      return fallback;
    }
  }
  return data?.detail || fallback;
}

// ── payment day picker ────────────────────────────────────────────────────────
function PaymentDayPicker({ value, onChange, graceDays = 3, name }) {
  const [selectedDay, setSelectedDay] = useState(value || 5);
  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const deadlineDay = selectedDay + graceDays > 31
    ? `${selectedDay + graceDays - 31} (next month)`
    : selectedDay + graceDays;

  const handleDayClick = (day) => {
    setSelectedDay(day);
    onChange?.(day);
  };

  return (
    <div className="space-y-2">
      <input type="hidden" name={name} value={selectedDay} />
      <div className="bg-white border border-gray-200 rounded-lg p-3">
        <div className="grid grid-cols-7 gap-1">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
            <div key={i} className="text-center text-xs font-medium text-gray-400 py-1">{d}</div>
          ))}
          {days.map((day) => {
            const isSelected = day === selectedDay;
            const isGrace = day > selectedDay && day <= selectedDay + graceDays;
            return (
              <button
                key={day}
                type="button"
                onClick={() => handleDayClick(day)}
                className={`w-8 h-8 rounded-full text-sm font-medium transition-all ${
                  isSelected ? 'bg-teal-600 text-white shadow ring-2 ring-teal-300'
                    : isGrace ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                {day}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-teal-600 inline-block"></span> Due day
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-orange-100 border border-orange-300 inline-block"></span> Grace period
          </span>
        </div>
        <span className="text-gray-500">Deadline: Day <strong>{deadlineDay}</strong></span>
      </div>
      <p className="text-xs text-gray-400 bg-gray-50 rounded p-2">
        <strong>Recurring:</strong> Day {selectedDay} every month
        {selectedDay > 28 && <span className="text-orange-500"> (adjusts for shorter months)</span>}
      </p>
    </div>
  );
}

// ── status pill ───────────────────────────────────────────────────────────────
function StatusPill({ status }) {
  const { t } = useTranslation('contracts');
  const meta = {
    active:             { label: t('status.active'),             cls: 'text-green-700 bg-green-50  ring-green-200' },
    pending:            { label: t('status.pending'),            cls: 'text-teal-700  bg-teal-50   ring-teal-200'  },
    pending_signature:  { label: t('status.pending_signature'),  cls: 'text-violet-700 bg-violet-50 ring-violet-200'},
    expiring_soon:      { label: t('status.expiring_soon'),      cls: 'text-amber-700 bg-amber-50  ring-amber-200' },
    expired:            { label: t('status.expired'),             cls: 'text-red-700   bg-red-50    ring-red-200'   },
    terminated:         { label: t('status.terminated'),         cls: 'text-stone-600 bg-stone-100 ring-stone-200' },
    draft:              { label: t('status.draft'),               cls: 'text-stone-600 bg-stone-100 ring-stone-200' },
  };
  const m = meta[status] ?? { label: status, cls: 'text-stone-600 bg-stone-100 ring-stone-200' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${m.cls}`}>
      {m.label}
    </span>
  );
}

// ── days until expiry ─────────────────────────────────────────────────────────
function daysUntil(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
}

// ── contract list item ────────────────────────────────────────────────────────
function ContractItem({ contract, selected, onClick, grouped = false }) {
  const { t } = useTranslation('contracts');
  const days = daysUntil(contract.end_date);
  const tenantName = contract.renters?.length
    ? contract.renters.find(r => r.is_primary)?.first_name
      ? `${contract.renters.find(r => r.is_primary)?.first_name} ${contract.renters.find(r => r.is_primary)?.last_name}`
      : `${contract.renters[0].first_name} ${contract.renters[0].last_name}`
    : contract.renter_name || '—';

  const contractNum = contract.contract_number || t('contractNumberFallback', { id: contract.id });

  return (
    <button
      onClick={onClick}
      className={`w-full text-left border-b border-stone-100 transition-colors ${
        grouped ? 'px-4 py-2.5 pl-8' : 'px-4 py-3'
      } ${selected ? 'bg-teal-50/80' : 'hover:bg-stone-50'}`}
    >
      <div className="flex items-start justify-between gap-2 mb-0.5">
        <span className="text-[13px] font-semibold text-stone-900 truncate">{contractNum}</span>
        <StatusPill status={contract.status} />
      </div>
      <p className="text-[12px] text-stone-600 truncate">{tenantName}</p>
      <div className="flex items-center justify-between mt-1">
        {!grouped && (
          <span className="text-[11px] text-stone-400">
            {contract.room_number ? t('roomPrefix', { number: contract.room_number }) : '—'}
            {contract.building_name ? ` · ${contract.building_name}` : ''}
          </span>
        )}
        {days !== null && contract.status !== 'terminated' && (
          <span className={`text-[11px] font-medium ml-auto ${
            days < 0 ? 'text-red-500' :
            days <= 30 ? 'text-amber-600' : 'text-stone-400'
          }`}>
            {days < 0
              ? t('daysRemaining.overdue', { days: -days })
              : days === 0
                ? t('daysRemaining.today')
                : t('daysRemaining.remaining', { days })}
          </span>
        )}
      </div>
    </button>
  );
}

// ── payment schedule grid ─────────────────────────────────────────────────────
function PaymentSchedule({ contract }) {
  const { t, i18n } = useTranslation('contracts');
  const lang = i18n.language?.startsWith('en') ? 'en' : 'vi';
  const locale = intlLocale(lang);

  // Shares the cache key with PrepaymentManager, so recording a block updates this too.
  const { data: prepayments = [] } = useQuery({
    queryKey: ['contract-prepayments', contract?.id],
    queryFn: () => api.get(`/api/admin/contracts/${contract.id}/prepayments`).then(r => r.data?.prepayments ?? []),
    enabled: !!contract?.id,
    staleTime: 30 * 1000,
  });

  if (!contract.start_date || !contract.end_date || !contract.monthly_rent) return null;

  const start = new Date(contract.start_date);
  const end   = new Date(contract.end_date);
  const months = [];
  const cur = new Date(start.getFullYear(), start.getMonth(), 1);
  while (cur <= end && months.length < 24) {
    months.push(new Date(cur));
    cur.setMonth(cur.getMonth() + 1);
  }

  // month key -> what a prepaid block does to it
  const ymOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  const monthsBetween = (a, b) => {
    const [ay, am] = a.split('-').map(Number);
    const [by, bm] = b.split('-').map(Number);
    return (by * 12 + bm) - (ay * 12 + am);
  };
  const blockFor = (key) => {
    for (const p of prepayments) {
      if (p.status !== 'active') continue;
      const off = monthsBetween(p.start_month, key);
      if (off >= 0 && off < p.months) return { block: p, offset: off };
    }
    return null;
  };

  return (
    <div className="grid grid-cols-4 gap-1">
      {months.map((m, i) => {
        const hit = blockFor(ymOf(m));
        const label = m.toLocaleDateString(locale, { month: 'short', year: '2-digit' });

        // Covered by an earlier month's charge — nothing due but utilities.
        if (hit && hit.offset > 0) {
          return (
            <div key={i} className="px-2 py-1.5 rounded-md bg-teal-50/60 border border-teal-100 text-center">
              <p className="text-[10px] text-teal-500">{label}</p>
              <p className="text-[11px] font-semibold text-teal-700 mt-0.5">
                {t('schedule.prepaidCovered', 'đã trả trước')}
              </p>
            </div>
          );
        }

        // The block's billing month carries the whole thing (rent + services − discount).
        if (hit) {
          return (
            <div key={i} className="px-2 py-1.5 rounded-md bg-teal-50 border border-teal-200 text-center">
              <p className="text-[10px] text-teal-600">{label} · ×{hit.block.months}</p>
              <p className="text-[11px] font-bold text-teal-800 mt-0.5">
                {(Number(hit.block.total_amount || 0) / 1e6).toFixed(1)}{t('schedule.millionSuffix')}
              </p>
            </div>
          );
        }

        return (
          <div key={i} className="px-2 py-1.5 rounded-md bg-stone-50 border border-stone-100 text-center">
            <p className="text-[10px] text-stone-400">{label}</p>
            <p className="text-[11px] font-semibold text-stone-700 mt-0.5">
              {(contract.monthly_rent / 1e6).toFixed(1)}{t('schedule.millionSuffix')}
            </p>
          </div>
        );
      })}
    </div>
  );
}

// ── create contract modal (full featured) ─────────────────────────────────────
function CreateContractModal({ open, onClose }) {
  const qc = useQueryClient();
  const { t } = useTranslation(['contracts', 'common']);

  // Form state
  const [selectedBuildingId, setSelectedBuildingId] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedRoomId, setSelectedRoomId] = useState('');
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [primaryTenantId, setPrimaryTenantId] = useState(null);
  const [monthlyRent, setMonthlyRent] = useState('');
  const [depositAmount, setDepositAmount] = useState('');
  const [contractServicesConfig, setContractServicesConfig] = useState([]);
  // Discount / special billing
  const [rentWaived, setRentWaived] = useState(false);
  const [firstMonthDiscount, setFirstMonthDiscount] = useState('');
  const [commissionAmount, setCommissionAmount] = useState('');

  // Reset when closed
  useEffect(() => {
    if (!open) {
      setSelectedBuildingId('');
      setSelectedTemplateId('');
      setSelectedRoomId('');
      setSelectedTenants([]);
      setPrimaryTenantId(null);
      setMonthlyRent('');
      setDepositAmount('');
      setContractServicesConfig([]);
      setRentWaived(false);
      setFirstMonthDiscount('');
      setCommissionAmount('');
    }
  }, [open]);

  // Fetch data
  const { data: templates = [] } = useQuery({
    queryKey: ['contract-templates'],
    queryFn: () => contractService.getTemplates().then(d => Array.isArray(d) ? d : []),
    enabled: open,
    staleTime: 0,
  });

  const { data: buildings = [] } = useQuery({
    queryKey: ['buildings-list'],
    queryFn: () => api.get('/api/properties/buildings').then(r => {
      const d = r.data; return Array.isArray(d) ? d : (d?.buildings ?? []);
    }),
    enabled: open,
    staleTime: 10 * 60 * 1000,
  });

  const { data: allRooms = [] } = useQuery({
    queryKey: ['rooms-all'],
    queryFn: () => api.get('/api/properties/rooms').then(r => {
      const d = r.data; return Array.isArray(d) ? d : (d?.rooms ?? []);
    }),
    enabled: open,
    staleTime: 2 * 60 * 1000,
  });

  const { data: allContracts = [] } = useQuery({
    queryKey: ['contracts'],
    queryFn: () => api.get('/api/contracts').then(r => {
      const d = r.data; return Array.isArray(d) ? d : (d?.contracts ?? []);
    }),
    staleTime: 2 * 60 * 1000,
  });

  const { data: renters = [] } = useQuery({
    queryKey: ['renters-list'],
    queryFn: () => api.get('/api/renters').then(r => {
      const d = r.data; return Array.isArray(d) ? d : (d?.renters ?? []);
    }),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const generate = useMutation({
    mutationFn: (data) => api.post('/api/contracts/generate', data).then(r => r.data),
    onSuccess: async () => {
      await new Promise(res => setTimeout(res, 300));
      await qc.refetchQueries({ queryKey: ['contracts'] });
      toast.success(t('contracts:generate.successToast'));
      onClose();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || t('contracts:generate.errorToast'));
    },
  });

  // Derived data
  // Only a live contract holds a room/renter. Ended states (expired, terminated,
  // renewed — the old contract of a renewal) free them up for reassignment.
  // Allow-list, mirroring the contract-service reconciliation query, so a status
  // added later can never silently lock a room out of the picker.
  const LIVE_CONTRACT_STATUSES = ['active', 'expiring_soon', 'pending_signature', 'draft'];
  const roomsWithActiveContracts = useMemo(() => {
    const s = new Set();
    allContracts.forEach(c => {
      if (LIVE_CONTRACT_STATUSES.includes(c.status) && c.room_id) s.add(c.room_id);
    });
    return s;
  }, [allContracts]);

  const rentersWithActiveContracts = useMemo(() => {
    const s = new Set();
    allContracts.forEach(c => {
      if (LIVE_CONTRACT_STATUSES.includes(c.status)) {
        if (c.renter_id) s.add(c.renter_id);
        c.renters?.forEach(r => s.add(r.id));
      }
    });
    return s;
  }, [allContracts]);

  const availableRooms = useMemo(() =>
    allRooms.filter(r => !roomsWithActiveContracts.has(r.id)), [allRooms, roomsWithActiveContracts]);

  const filteredRooms = useMemo(() =>
    selectedBuildingId
      ? availableRooms.filter(r => r.building_id === parseInt(selectedBuildingId))
      : [], [availableRooms, selectedBuildingId]);

  const availableRenters = useMemo(() =>
    renters.filter(r => !rentersWithActiveContracts.has(r.id)), [renters, rentersWithActiveContracts]);

  const selectedBuildingData = useMemo(() =>
    buildings.find(b => b.id === parseInt(selectedBuildingId)), [buildings, selectedBuildingId]);

  const selectedTemplateData = useMemo(() =>
    templates.find(t => t.id === parseInt(selectedTemplateId)), [templates, selectedTemplateId]);

  // Auto-merge building + template services
  useEffect(() => {
    if (!selectedBuildingId && !selectedTemplateId) {
      setContractServicesConfig([]);
      return;
    }

    const convertLegacy = (pricing) => {
      if (!pricing || typeof pricing !== 'object') return [];
      return Object.entries(pricing).map(([key, value]) => ({
        service_id: key,
        name: value.label || value.name || key.replace(/_/g, ' '),
        category: 'optional',
        type: value.unit === 'kWh' || value.unit === 'm3' ? 'metered'
          : value.per === 'person' ? 'fixed_per_person' : 'fixed_per_room',
        unit_price: value.rate || value.price || 0,
        unit: value.unit || value.per || 'month',
        is_active: value.is_active !== false,
        is_included: value.is_included || false,
      }));
    };

    let buildingServices = [];
    if (selectedBuildingData) {
      buildingServices = Array.isArray(selectedBuildingData.services_config) && selectedBuildingData.services_config.length
        ? selectedBuildingData.services_config.map(s => ({ ...s }))
        : convertLegacy(selectedBuildingData.utility_pricing);
    }

    let templateServices = [];
    if (selectedTemplateData) {
      templateServices = Array.isArray(selectedTemplateData.services_config) && selectedTemplateData.services_config.length
        ? selectedTemplateData.services_config.map(s => ({ ...s }))
        : convertLegacy(selectedTemplateData.utility_pricing);
    }

    const merged = [...buildingServices];
    templateServices.forEach(ts => {
      const idx = merged.findIndex(s => s.service_id === ts.service_id);
      if (idx >= 0) merged[idx] = { ...merged[idx], ...ts };
      else merged.push(ts);
    });
    setContractServicesConfig(merged);
  }, [selectedBuildingId, selectedTemplateId, selectedBuildingData, selectedTemplateData]);

  const handleToggleTenant = (id) => {
    if (selectedTenants.includes(id)) {
      setSelectedTenants(prev => prev.filter(x => x !== id));
      if (primaryTenantId === id) setPrimaryTenantId(null);
    } else {
      setSelectedTenants(prev => {
        if (prev.length === 0) setPrimaryTenantId(id);
        return [...prev, id];
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const primaryId = primaryTenantId || (selectedTenants[0] ?? null);
    generate.mutate({
      template_id: parseInt(fd.get('template_id')),
      tenant_id: primaryId,
      tenant_ids: selectedTenants.length > 0 ? selectedTenants : (primaryId ? [primaryId] : []),
      room_id: parseInt(fd.get('room_id')),
      start_date: fd.get('start_date'),
      monthly_rent: parseFloat(monthlyRent) || 0,
      deposit_amount: parseFloat(depositAmount) || 0,
      custom_terms: fd.get('custom_terms') || undefined,
      services_config: contractServicesConfig.length > 0 ? contractServicesConfig : undefined,
      rent_waived: rentWaived,
      first_month_discount: firstMonthDiscount ? parseFloat(firstMonthDiscount) : 0,
      commission_amount: commissionAmount ? parseFloat(commissionAmount) : 0,
    });
  };

  if (!open) return null;

  const inputCls = 'w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400';
  const labelCls = 'block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5';

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-4xl max-h-[92vh] flex flex-col rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-4 flex justify-between items-center flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold text-white">{t('contracts:generate.title')}</h2>
            <p className="text-teal-100 text-xs mt-0.5">{t('contracts:generate.subtitle')}</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white hover:bg-white/10 p-2 rounded-full transition-colors">
            <Icons.X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6 space-y-6">

            {/* Section 1: Template */}
            <div>
              <h3 className="text-sm font-semibold text-stone-800 mb-3 flex items-center gap-2">
                <Icons.FileCheck size={15} className="text-teal-500" /> {t('contracts:generate.section.configuration')}
              </h3>
              <label className={labelCls}>{t('contracts:generate.label.template')} <span className="text-red-500">*</span></label>
              <select name="template_id" required className={inputCls}
                value={selectedTemplateId}
                onChange={e => {
                  setSelectedTemplateId(e.target.value);
                  if (selectedRoomId && monthlyRent) {
                    const tmpl = templates.find(t => t.id === parseInt(e.target.value));
                    if (tmpl?.deposit_months) setDepositAmount((parseFloat(monthlyRent) * tmpl.deposit_months).toString());
                  }
                }}>
                <option value="">{t('contracts:generate.placeholder.template')}</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              {templates.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  {t('contracts:generate.noTemplates')}
                </p>
              )}
            </div>

            <div className="border-t border-stone-100" />

            {/* Section 2: Tenants */}
            <div>
              <h3 className="text-sm font-semibold text-stone-800 mb-3 flex items-center gap-2">
                <Icons.User size={15} className="text-purple-500" /> {t('contracts:generate.section.tenants')}
              </h3>
              <div className="bg-stone-50 rounded-xl p-3 border border-stone-100 max-h-52 overflow-y-auto space-y-1">
                {availableRenters.length === 0 ? (
                  <p className="text-xs text-stone-400 py-4 text-center">{t('contracts:generate.noAvailableRenters')}</p>
                ) : (
                  availableRenters.map(renter => (
                    <label key={renter.id}
                      className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-all ${
                        selectedTenants.includes(renter.id) ? 'bg-white shadow-sm ring-1 ring-teal-200' : 'hover:bg-white/70'
                      }`}>
                      <input type="checkbox"
                        checked={selectedTenants.includes(renter.id)}
                        onChange={() => handleToggleTenant(renter.id)}
                        className="w-4 h-4 text-teal-600 rounded border-stone-300" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-medium text-stone-900">{renter.first_name} {renter.last_name}</p>
                        <p className="text-[11px] text-stone-400">{renter.email}</p>
                      </div>
                      {selectedTenants.includes(renter.id) && (
                        <button type="button"
                          onClick={e => { e.preventDefault(); e.stopPropagation(); setPrimaryTenantId(renter.id); }}
                          className={`text-[11px] px-2.5 py-1 rounded-full font-medium transition-colors ${
                            primaryTenantId === renter.id
                              ? 'bg-teal-600 text-white'
                              : 'bg-stone-200 text-stone-600 hover:bg-stone-300'
                          }`}>
                          {primaryTenantId === renter.id ? t('contracts:generate.primaryLabel') : t('contracts:generate.setPrimaryLabel')}
                        </button>
                      )}
                    </label>
                  ))
                )}
              </div>
              <p className="text-xs text-stone-400 mt-1.5 px-1">
                {selectedTenants.length === 0 ? t('contracts:generate.selectAtLeastOne') : t('contracts:generate.selectedCount', { count: selectedTenants.length })}
              </p>
            </div>

            <div className="border-t border-stone-100" />

            {/* Section 3: Property */}
            <div>
              <h3 className="text-sm font-semibold text-stone-800 mb-3 flex items-center gap-2">
                <Icons.Home size={15} className="text-indigo-500" /> {t('contracts:generate.section.property')}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{t('contracts:create.label.building')} <span className="text-red-500">*</span></label>
                  <select required className={inputCls}
                    value={selectedBuildingId}
                    onChange={e => {
                      setSelectedBuildingId(e.target.value);
                      setSelectedRoomId('');
                      setMonthlyRent('');
                      setDepositAmount('');
                    }}>
                    <option value="">{t('contracts:create.label.buildingPlaceholder')}</option>
                    {buildings.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>{t('contracts:create.label.room')} <span className="text-red-500">*</span></label>
                  <select name="room_id" required disabled={!selectedBuildingId} className={inputCls}
                    value={selectedRoomId}
                    onChange={e => {
                      setSelectedRoomId(e.target.value);
                      if (e.target.value) {
                        const room = filteredRooms.find(r => r.id === parseInt(e.target.value));
                        if (room) {
                          setMonthlyRent(room.monthly_rent?.toString() || '');
                          const depositMonths = selectedTemplateData?.deposit_months || 2;
                          setDepositAmount(((room.monthly_rent || 0) * depositMonths).toString());
                        }
                      } else {
                        setMonthlyRent('');
                        setDepositAmount('');
                      }
                    }}>
                    <option value="">{selectedBuildingId ? t('contracts:generate.placeholder.chooseRoom') : t('contracts:generate.placeholder.selectBuildingFirst')}</option>
                    {filteredRooms.map(r => (
                      <option key={r.id} value={r.id}>
                        {r.floor
                          ? t('contracts:create.label.roomOptionWithFloor', { number: r.room_number, floor: r.floor })
                          : t('contracts:create.label.roomOption', { number: r.room_number })
                        } — {r.monthly_rent?.toLocaleString('vi-VN')}₫{t('contracts:generate.perMonth')}
                      </option>
                    ))}
                  </select>
                  {selectedBuildingId && filteredRooms.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">{t('contracts:generate.noAvailableRooms')}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="border-t border-stone-100" />

            {/* Section 4: Contract Terms */}
            <div>
              <h3 className="text-sm font-semibold text-stone-800 mb-3 flex items-center gap-2">
                <Icons.DollarSign size={15} className="text-green-500" /> {t('contracts:generate.section.contractTerms')}
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className={labelCls}>{t('contracts:create.label.startDate')} <span className="text-red-500">*</span></label>
                  <input type="date" name="start_date" required className={inputCls}
                    defaultValue={new Date().toISOString().split('T')[0]} />
                </div>
                <div>
                  <label className={labelCls}>
                    {t('contracts:create.label.monthlyRent')} <span className="text-red-500">*</span>
                    {selectedRoomId && <span className="text-green-600 normal-case font-normal ml-1">{t('contracts:generate.fromRoom')}</span>}
                  </label>
                  <CurrencyInput required className={inputCls}
                    value={monthlyRent}
                    onChange={e => setMonthlyRent(e.target.value)}
                    placeholder={selectedRoomId ? '' : t('contracts:generate.placeholder.selectRoomFirst')} />
                </div>
                <div>
                  <label className={labelCls}>
                    {t('contracts:create.label.deposit')} <span className="text-red-500">*</span>
                    {selectedTemplateData && <span className="text-green-600 normal-case font-normal ml-1">{t('contracts:generate.depositMonths', { months: selectedTemplateData.deposit_months || 2 })}</span>}
                  </label>
                  <CurrencyInput required className={inputCls}
                    value={depositAmount}
                    onChange={e => setDepositAmount(e.target.value)}
                    placeholder="0" />
                </div>
              </div>
            </div>

            <div className="border-t border-stone-100" />

            {/* Section 5: Payment Schedule */}
            <div>
              <h3 className="text-sm font-semibold text-stone-800 mb-3 flex items-center gap-2">
                <Icons.Calendar size={15} className="text-orange-500" /> {t('contracts:generate.section.paymentSchedule')}
              </h3>

              {/* Building payment method badge */}
              {selectedBuildingData && (
                <div className={`mb-4 p-3 rounded-lg border flex items-center justify-between ${
                  selectedBuildingData.payment_method === 'pay_later'
                    ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'
                }`}>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      selectedBuildingData.payment_method === 'pay_later'
                        ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'
                    }`}>
                      {selectedBuildingData.payment_method === 'pay_later' ? t('contracts:generate.payLater') : t('contracts:generate.payFirst')}
                    </span>
                    <span className="text-xs text-stone-600">
                      {selectedBuildingData.payment_method === 'pay_later'
                        ? t('contracts:generate.payLaterDesc')
                        : t('contracts:generate.payFirstDesc')}
                    </span>
                  </div>
                  <span className="text-[11px] text-stone-400 italic">{t('contracts:generate.fromBuildingSettings')}</span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className={labelCls}>{t('contracts:generate.label.paymentDueDay')}</label>
                  <PaymentDayPicker
                    value={selectedBuildingData?.billing_day || 5}
                    graceDays={3}
                    name="payment_due_day"
                  />
                </div>
                <div>
                  <label className={labelCls}>{t('contracts:generate.label.gracePeriod')}</label>
                  <input type="number" name="payment_grace_days" defaultValue={3} min="0" max="15"
                    className={`${inputCls} w-32`} />
                  <p className="text-[11px] text-stone-400 mt-1">{t('contracts:generate.gracePeriodHelp')}</p>
                  {selectedTemplateData && (
                    <div className="mt-3 p-2.5 bg-stone-50 rounded-lg text-xs text-stone-600 space-y-0.5">
                      <p><strong>{t('contracts:generate.templateDefaults')}</strong></p>
                      <p>{t('contracts:generate.templateDuration', { months: selectedTemplateData.duration_months })}</p>
                      <p>{t('contracts:generate.templateLateFee', { percent: selectedTemplateData.late_fee_percentage })}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="border-t border-stone-100" />

            {/* Section 6: Services Config */}
            {(selectedBuildingId || selectedTemplateId) && (
              <div>
                <h3 className="text-sm font-semibold text-stone-800 mb-1 flex items-center gap-2">
                  <Icons.DollarSign size={15} className="text-amber-500" /> {t('contracts:generate.section.services')}
                </h3>
                <p className="text-[11px] text-amber-600 mb-3">
                  {t('contracts:generate.servicesInheritedNote')}
                </p>
                <ServiceConfigManager
                  services={contractServicesConfig}
                  onChange={setContractServicesConfig}
                  title={t('contracts:generate.contractServices')}
                />
              </div>
            )}

            <div className="border-t border-stone-100" />

            {/* Section 7: Discounts & Special Billing */}
            <div>
              <h3 className="text-sm font-semibold text-stone-800 mb-1 flex items-center gap-2">
                <Icons.Tag size={15} className="text-pink-500" /> {t('contracts:generate.section.discounts')}
              </h3>
              <p className="text-[11px] text-stone-400 mb-4">
                {t('contracts:generate.discountsNote')}
              </p>

              {/* Rent waived toggle */}
              <label className="flex items-start gap-3 p-3 rounded-lg bg-rose-50 border border-rose-100 cursor-pointer mb-4 hover:bg-rose-100/60 transition-colors">
                <div className="mt-0.5">
                  <input
                    type="checkbox"
                    checked={rentWaived}
                    onChange={e => setRentWaived(e.target.checked)}
                    className="w-4 h-4 text-rose-600 rounded border-stone-300"
                  />
                </div>
                <div>
                  <p className="text-[13px] font-semibold text-rose-800">{t('contracts:detail.billing.rentWaivedLabel')}</p>
                  <p className="text-[11px] text-rose-600 mt-0.5">
                    {t('contracts:generate.rentWaivedDesc')}
                  </p>
                </div>
              </label>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{t('contracts:detail.billing.firstMonthDiscountLabel')}</label>
                  <CurrencyInput
                    className={inputCls}
                    value={firstMonthDiscount}
                    onChange={e => setFirstMonthDiscount(e.target.value)}
                    placeholder="0 — no discount"
                    disabled={rentWaived}
                  />
                  <p className="text-[11px] text-stone-400 mt-1">
                    {t('contracts:generate.firstMonthDiscountHelp')}
                  </p>
                </div>
                <div>
                  <label className={labelCls}>{t('contracts:detail.billing.commissionLabel')}</label>
                  <CurrencyInput
                    className={inputCls}
                    value={commissionAmount}
                    onChange={e => setCommissionAmount(e.target.value)}
                    placeholder="0 — no commission"
                  />
                  <p className="text-[11px] text-stone-400 mt-1">
                    {t('contracts:generate.commissionHelp')}
                  </p>
                </div>
              </div>

              {/* Summary preview */}
              {(rentWaived || parseFloat(firstMonthDiscount) > 0 || parseFloat(commissionAmount) > 0) && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-100 rounded-lg text-[11px] space-y-1 text-amber-800">
                  <p className="font-semibold mb-2 text-amber-900">{t('contracts:generate.invoicePreview')}</p>
                  {rentWaived ? (
                    <p>{t('contracts:generate.baseRentLine')} <span className="line-through text-stone-400">{VND(parseFloat(monthlyRent) || 0)}</span> <strong>₫0</strong> {t('contracts:generate.waived')}</p>
                  ) : (
                    <p>{t('contracts:generate.baseRentLine')} <strong>{VND(parseFloat(monthlyRent) || 0)}</strong></p>
                  )}
                  {!rentWaived && parseFloat(firstMonthDiscount) > 0 && (
                    <p>{t('contracts:generate.firstMonthDiscountLine')} <strong className="text-rose-600">−{VND(parseFloat(firstMonthDiscount))}</strong></p>
                  )}
                  {parseFloat(commissionAmount) > 0 && (
                    <p>{t('contracts:generate.commissionLine')} <strong className="text-rose-600">−{VND(parseFloat(commissionAmount))}</strong></p>
                  )}
                  <p>{t('contracts:generate.servicesNormal')}</p>
                </div>
              )}
            </div>

            <div className="border-t border-stone-100" />

            {/* Section 8: Additional Terms */}
            <div>
              <label className={labelCls}>{t('contracts:generate.label.additionalTerms')}</label>
              <textarea name="custom_terms" rows={3}
                className="w-full px-3 py-2 rounded-lg border border-stone-200 text-[13px] font-mono resize-none focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
                placeholder={t('contracts:generate.placeholder.additionalTerms')} />
            </div>

          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-stone-100 flex justify-end gap-3 bg-stone-50/50 flex-shrink-0">
            <button type="button" onClick={onClose}
              className="px-4 h-9 rounded-lg border border-stone-200 text-[13px] font-medium text-stone-700 hover:bg-stone-50 transition-colors">
              {t('common:action.cancel')}
            </button>
            <button type="submit"
              disabled={generate.isPending || selectedTenants.length === 0 || !selectedTemplateId}
              className="px-5 h-9 rounded-lg bg-teal-600 text-white text-[13px] font-medium hover:bg-teal-700 transition-colors disabled:opacity-50">
              {generate.isPending ? t('contracts:generate.creating') : t('contracts:generate.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── contract detail panel ─────────────────────────────────────────────────────
function ContractDetail({ contract, onTerminate, onRenew, onDelete, onEdit, onActivate, onRevertToDraft, onMoveOut, onMoveIn, onRecover, activating, reverting }) {
  const { t, i18n } = useTranslation('contracts');
  const lang = i18n.language?.startsWith('en') ? 'en' : 'vi';
  const qc = useQueryClient();

  // Fetch individual contract for terminated ones — the list payload lacks termination reason
  // (reason lives in MongoDB, only returned by the single-contract GET endpoint).
  const { data: detailContract } = useQuery({
    queryKey: ['contract-detail', contract?.id],
    queryFn: () => api.get(`/api/contracts/${contract.id}`).then(r => r.data),
    enabled: !!contract && (contract.status === 'terminated' || (contract.status === 'expiring_soon' && !!contract.terminated_at)),
    staleTime: 60 * 1000,
  });
  const terminationDetails = detailContract?.termination_details ?? contract?.termination_details;
  const terminatedAt = terminationDetails?.terminated_at ?? contract?.terminated_at;
  const terminationReason = terminationDetails?.reason;

  // Billing & discounts inline edit state
  const [editingBilling, setEditingBilling] = useState(false);
  const [billingDraft, setBillingDraft] = useState({});

  // Services inline edit state (active contracts only)
  const [editingServices, setEditingServices] = useState(false);
  const [servicesDraft, setServicesDraft] = useState([]);


  const patchContract = useMutation({
    mutationFn: (data) => api.put(`/api/contracts/${contract.id}`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contracts'] });
      setEditingBilling(false);
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Update failed'),
  });

  const syncCommission = useMutation({
    mutationFn: () => api.post(`/api/contracts/${contract.id}/commission-expense`).then(r => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['expenses'] });
      const monthSuffix = data.billing_month ? ` (tháng ${data.billing_month})` : '';
      const msg = {
        created: `Đã tạo chi phí hoa hồng${monthSuffix}`,
        updated: `Đã cập nhật chi phí hoa hồng${monthSuffix}`,
        unchanged: `Chi phí hoa hồng đã tồn tại${monthSuffix} — xem trong tab Chi phí tháng đó`,
        skipped: data.reason,
      }[data.action] ?? data.action;
      toast.success(msg);
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Tạo chi phí thất bại'),
  });

  const syncRenters = useMutation({
    mutationFn: () => api.post(`/api/contracts/${contract.id}/sync-renters`).then(r => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['contracts'] });
      toast.success(
        data.synced > 0
          ? `Đã thêm ${data.synced} người thuê phụ từ hợp đồng gốc. Hóa đơn đang tính lại.`
          : 'Tất cả người thuê đã được đồng bộ — không có gì mới.'
      );
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Đồng bộ người thuê thất bại'),
  });

  const syncServices = useMutation({
    mutationFn: () => api.post(`/api/contracts/${contract.id}/sync-services`).then(r => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['contracts'] });
      toast.success(
        data.payments_recalculated > 0
          ? `Đã tính lại ${data.payments_recalculated} hóa đơn theo cấu hình hiện tại`
          : 'Không có hóa đơn pending nào cần cập nhật'
      );
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Đồng bộ dịch vụ thất bại'),
  });

  // Expense categories for commission mapping
  const { data: expenseCategories = [] } = useQuery({
    queryKey: ['expense-categories'],
    queryFn: () => api.get('/api/expenses/categories/standard').then(r => Array.isArray(r.data) ? r.data : []),
    enabled: !!contract,
    staleTime: 5 * 60 * 1000,
  });

  // ── PDF export ──────────────────────────────────────────────────────────────
  const [exportMenuOpen, setExportMenuOpen] = useState(false);

  // Word templates can be filled with this contract's data and converted to PDF
  const { data: allTemplates = [] } = useQuery({
    queryKey: ['contract-templates'],
    queryFn: () => contractService.getTemplates().then(d => Array.isArray(d) ? d : []),
    enabled: !!contract,
    staleTime: 5 * 60 * 1000,
  });
  const wordTemplates = allTemplates.filter(tp => tp.file_path);

  const exportPdf = useMutation({
    mutationFn: ({ documentType, templateId }) =>
      templateId
        ? contractService.exportFromTemplate(contract.id, templateId, 'pdf')
        : contractService.exportContractPdf(contract.id, documentType),
    onSuccess: (data, vars) => {
      const base = vars.documentType === 'asset_handover' ? 'bien-ban-ban-giao' : 'hop-dong';
      const num = safeFilePart(contract.contract_number || contract.id);
      saveBlob(data, `${base}_${num}.pdf`, 'application/pdf');
      toast.success('Đã xuất PDF');
    },
    onError: async (err) => toast.error(await blobErrorMessage(err, 'Xuất PDF thất bại')),
  });

  const runExport = (vars) => {
    setExportMenuOpen(false);
    exportPdf.mutate(vars);
  };

  const startEditBilling = () => {
    setBillingDraft({
      rent_waived: contract.rent_waived ?? false,
      first_month_discount: contract.first_month_discount ?? 0,
      commission_amount: contract.commission_amount ?? 0,
      commission_expense_category: contract.commission_expense_category ?? '',
    });
    setEditingBilling(true);
  };

  if (!contract) {
    return (
      <div className="flex-1 flex items-center justify-center text-stone-400">
        <EmptyState
          icon={<Icons.FileCheck />}
          title={t('detail.selectTitle')}
          description={t('detail.selectDesc')}
        />
      </div>
    );
  }

  const tenants = contract.renters?.length
    ? contract.renters
    : contract.renter_name
      ? [{ first_name: contract.renter_name, last_name: '', is_primary: true }]
      : [];

  const primaryTenant = tenants.find(r => r.is_primary) ?? tenants[0];
  const tenantName = primaryTenant
    ? `${primaryTenant.first_name} ${primaryTenant.last_name}`.trim()
    : '—';

  const days = daysUntil(contract.end_date);
  const totalMonths = contract.start_date && contract.end_date
    ? Math.ceil((new Date(contract.end_date) - new Date(contract.start_date)) / (1000 * 60 * 60 * 24 * 30))
    : null;

  const services = (() => {
    let s = contract.services_config;
    if (typeof s === 'string') try { s = JSON.parse(s); } catch { s = []; }
    return Array.isArray(s) ? s : [];
  })();

  const contractNum = contract.contract_number || t('contractNumberFallback', { id: contract.id });

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-stone-200 px-6 py-4 flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-lg font-semibold text-stone-900">{contractNum}</h2>
            <StatusPill status={contract.status} />
          </div>
          <p className="text-sm text-stone-500">
            {t('detail.headerSubtitle', {
              tenant: tenantName,
              room: contract.room_number || '—',
              building: contract.building_name || '',
            })}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Export to PDF */}
          <div className="relative">
            <button
              onClick={() => setExportMenuOpen(o => !o)}
              disabled={exportPdf.isPending}
              className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-stone-200 text-stone-700 text-[13px] font-medium hover:bg-stone-50 transition-colors disabled:opacity-50"
              title="Tải hợp đồng dưới dạng PDF"
            >
              <Icons.Download size={14} />
              {exportPdf.isPending ? 'Đang xuất…' : 'Xuất PDF'}
              <Icons.ChevronDown size={13} className="text-stone-400" />
            </button>
            {exportMenuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setExportMenuOpen(false)} />
                <div className="absolute right-0 mt-1 z-20 w-64 rounded-lg border border-stone-200 bg-white shadow-lg py-1">
                  <button
                    onClick={() => runExport({ documentType: 'contract' })}
                    className="w-full text-left px-3 py-2 text-[13px] text-stone-700 hover:bg-stone-50 flex items-center gap-2"
                  >
                    <Icons.Document size={14} className="text-stone-400" />
                    Hợp đồng thuê (PDF)
                  </button>
                  <button
                    onClick={() => runExport({ documentType: 'asset_handover' })}
                    className="w-full text-left px-3 py-2 text-[13px] text-stone-700 hover:bg-stone-50 flex items-center gap-2"
                  >
                    <Icons.FileCheck size={14} className="text-stone-400" />
                    Biên bản bàn giao tài sản (PDF)
                  </button>
                  {wordTemplates.length > 0 && (
                    <>
                      <div className="border-t border-stone-100 my-1" />
                      <p className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-stone-400">
                        Từ mẫu Word
                      </p>
                      {wordTemplates.map(tp => (
                        <button
                          key={tp.id}
                          onClick={() => runExport({ documentType: 'contract', templateId: tp.id })}
                          className="w-full text-left px-3 py-2 text-[13px] text-stone-700 hover:bg-stone-50 flex items-center gap-2"
                        >
                          <Icons.Layers size={14} className="text-stone-400 flex-shrink-0" />
                          <span className="truncate">{tp.name}</span>
                        </button>
                      ))}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
          {['draft', 'pending', 'pending_signature'].includes(contract.status) && onEdit && (
            <button onClick={() => onEdit(contract)}
              className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-stone-200 text-stone-700 text-[13px] font-medium hover:bg-stone-50 transition-colors">
              <Icons.Edit size={14} />
              {t('detail.edit')}
            </button>
          )}
          {['draft', 'pending', 'pending_signature'].includes(contract.status) && onActivate && (
            <button
              onClick={() => onActivate(contract)}
              disabled={activating}
              className="flex items-center gap-1.5 px-3 h-9 rounded-lg bg-teal-600 text-white text-[13px] font-medium hover:bg-teal-700 transition-colors disabled:opacity-50">
              <Icons.CheckCircle size={14} />
              {activating ? 'Đang kích hoạt…' : 'Kích hoạt'}
            </button>
          )}
          {['active', 'expiring_soon'].includes(contract.status) && onRevertToDraft && (
            <button
              onClick={() => onRevertToDraft(contract)}
              disabled={reverting}
              className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-amber-300 text-amber-700 text-[13px] font-medium hover:bg-amber-50 transition-colors disabled:opacity-50">
              <Icons.Refresh size={14} />
              {reverting ? 'Đang hoàn tác…' : 'Hoàn tác về nháp'}
            </button>
          )}
          {['active', 'expiring_soon'].includes(contract.status) && contract.commission_amount > 0 && (
            <button
              onClick={() => syncCommission.mutate()}
              disabled={syncCommission.isPending}
              className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-stone-200 text-stone-600 text-[13px] font-medium hover:bg-stone-50 transition-colors disabled:opacity-50"
              title="Tạo hoặc cập nhật chi phí hoa hồng trong Tài chính"
            >
              <Icons.Receipt size={14} />
              {syncCommission.isPending ? 'Đang tạo…' : 'Chi phí HH'}
            </button>
          )}
          {['active', 'expiring_soon'].includes(contract.status) && (
            <button
              onClick={() => syncRenters.mutate()}
              disabled={syncRenters.isPending}
              className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-purple-200 text-purple-700 text-[13px] font-medium hover:bg-purple-50 transition-colors disabled:opacity-50"
              title="Sao chép người thuê từ hợp đồng gốc (dùng khi gia hạn bị thiếu người thuê)"
            >
              <Icons.Users size={14} />
              {syncRenters.isPending ? 'Đang đồng bộ…' : 'Đồng bộ người thuê'}
            </button>
          )}
          {['active', 'expiring_soon'].includes(contract.status) && (
            <button
              onClick={() => syncServices.mutate()}
              disabled={syncServices.isPending}
              className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-blue-200 text-blue-700 text-[13px] font-medium hover:bg-blue-50 transition-colors disabled:opacity-50"
              title="Tính lại hóa đơn pending theo cấu hình dịch vụ hiện tại"
            >
              <Icons.Cog size={14} />
              {syncServices.isPending ? 'Đang đồng bộ…' : 'Đồng bộ dịch vụ'}
            </button>
          )}
          {['active', 'expiring_soon', 'expired'].includes(contract.status) && onRenew && (
            <button onClick={() => onRenew(contract)}
              className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-teal-200 text-teal-700 text-[13px] font-medium hover:bg-teal-50 transition-colors">
              <Icons.Refresh size={14} />
              {t('detail.renew')}
            </button>
          )}
          {['active', 'expiring_soon'].includes(contract.status) && onMoveIn && (
            <button onClick={() => onMoveIn(contract)}
              className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-teal-200 text-teal-700 text-[13px] font-medium hover:bg-teal-50 transition-colors">
              <Icons.UserPlus size={14} />
              Thêm người thuê
            </button>
          )}
          {['active', 'expiring_soon'].includes(contract.status) && tenants.filter(t => !t.moved_out_at).length > 1 && onMoveOut && (
            <button onClick={() => onMoveOut(contract)}
              className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-orange-200 text-orange-600 text-[13px] font-medium hover:bg-orange-50 transition-colors">
              <Icons.UserPlus size={14} />
              Rời phòng
            </button>
          )}
          {['active', 'expiring_soon'].includes(contract.status) && (
            <button onClick={() => onTerminate(contract)}
              className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-red-200 text-red-600 text-[13px] font-medium hover:bg-red-50 transition-colors">
              <Icons.XCircle size={14} />
              {t('detail.terminate')}
            </button>
          )}
          {contract.status === 'terminated' && onRecover && (
            <button onClick={() => onRecover(contract)}
              className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-amber-300 text-amber-700 text-[13px] font-medium hover:bg-amber-50 transition-colors">
              <Icons.Refresh size={14} />
              Khôi phục
            </button>
          )}
          {!['active', 'expiring_soon'].includes(contract.status) && onDelete && (
            <button onClick={() => onDelete(contract)}
              className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-red-200 text-red-600 text-[13px] font-medium hover:bg-red-50 transition-colors">
              <Icons.Trash size={14} />
              {t('detail.delete')}
            </button>
          )}
        </div>
      </div>

      {/* Terminated — red banner */}
      {contract.status === 'terminated' && (
        <div className="mx-4 mt-3 mb-1 rounded-xl bg-red-600 text-white px-4 py-3 space-y-1.5">
          <div className="flex items-center gap-2">
            <Icons.XCircle size={15} className="flex-shrink-0" />
            <span className="text-[13px] font-bold uppercase tracking-wide">Hợp đồng đã kết thúc</span>
            {terminatedAt && (
              <span className="ml-auto text-[12px] font-semibold opacity-90">
                {fmtDate(terminatedAt, lang)}
              </span>
            )}
          </div>
          {terminationReason && (
            <p className="text-[12px] opacity-85 pl-[23px] leading-relaxed">{terminationReason}</p>
          )}
        </div>
      )}

      {/* Planned termination — orange banner for expiring_soon with agreed move-out date */}
      {contract.status === 'expiring_soon' && contract.terminated_at && (
        <div className="mx-4 mt-3 mb-1 rounded-xl bg-orange-500 text-white px-4 py-3 space-y-1.5">
          <div className="flex items-center gap-2">
            <Icons.Alert size={15} className="flex-shrink-0" />
            <span className="text-[13px] font-bold uppercase tracking-wide">Đã lên lịch kết thúc</span>
            <span className="ml-auto text-[12px] font-semibold opacity-90">
              {fmtDate(contract.terminated_at, lang)}
            </span>
          </div>
          {terminationReason && (
            <p className="text-[12px] opacity-85 pl-[23px] leading-relaxed">{terminationReason}</p>
          )}
        </div>
      )}

      <div className="px-6 py-4 space-y-0">
        {/* Tenant info */}
        <section className="py-4 border-b border-stone-100">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400 mb-3">{t('detail.section.tenant')}</p>
          {tenants.length === 0 ? (
            <p className="text-[13px] text-stone-400">{t('detail.section.tenantEmpty')}</p>
          ) : (
            <div className="space-y-2">
              {tenants.map((tn, i) => {
                const movedOut = !!tn.moved_out_at;
                const movedOutDate = tn.moved_out_at
                  ? new Date(tn.moved_out_at).toLocaleDateString(lang === 'vi' ? 'vi-VN' : 'en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
                  : null;
                return (
                  <div key={i} className={`flex items-center gap-3 py-1.5 ${movedOut ? 'opacity-50' : ''}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${movedOut ? 'bg-stone-100' : 'bg-teal-100'}`}>
                      <span className={`font-semibold text-[12px] ${movedOut ? 'text-stone-400' : 'text-teal-700'}`}>
                        {(tn.first_name || '?').charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-[13px] font-medium ${movedOut ? 'text-stone-400 line-through' : 'text-stone-900'}`}>
                        {tn.first_name} {tn.last_name}
                        {tn.is_primary && !movedOut && (
                          <span className="ml-2 text-[10px] bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded font-semibold">{t('detail.primaryTag')}</span>
                        )}
                      </p>
                      {movedOut
                        ? <p className="text-[11px] text-stone-400">Đã rời phòng: {movedOutDate}</p>
                        : <>
                            {tn.email && <p className="text-[11px] text-stone-400">{tn.email}</p>}
                            {tn.phone && <p className="text-[11px] text-stone-400">{tn.phone}</p>}
                          </>
                      }
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Contract terms */}
        <section className="py-4 border-b border-stone-100">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400 mb-3">{t('detail.section.terms')}</p>
          <dl className="space-y-1.5">
            {[
              [t('detail.kv.startDate'),  fmtDate(contract.start_date, lang)],
              [t('detail.kv.endDate'),    fmtDate(contract.end_date, lang)],
              ...(totalMonths ? [[t('detail.kv.duration'), t('detail.kv.durationValue', { months: totalMonths })]] : []),
              ...(days !== null && contract.status !== 'terminated' ? [
                [t('detail.kv.remaining'), days < 0
                  ? t('daysRemaining.overdueLong', { days: -days })
                  : t('daysRemaining.remainingLong', { days })]
              ] : []),
            ].map(([k, v]) => (
              <div key={k} className="flex items-baseline justify-between gap-4">
                <dt className="text-[12px] text-stone-500 flex-shrink-0">{k}</dt>
                <dd className="text-[13px] font-medium text-stone-900 text-right">{v}</dd>
              </div>
            ))}
          </dl>
        </section>

        {/* Financial */}
        <section className="py-4 border-b border-stone-100">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400 mb-3">{t('detail.section.financial')}</p>
          <dl className="space-y-1.5">
            {[
              [t('detail.kv.monthlyRent'),   VND(contract.monthly_rent, lang)],
              [t('detail.kv.deposit'),       VND(contract.deposit_amount, lang)],
              [t('detail.kv.paymentDueDay'), t('detail.kv.paymentDueValue', { day: contract.payment_due_day || 5 })],
              ...(contract.payment_grace_days ? [[t('detail.kv.graceDays'), t('detail.kv.graceDaysValue', { days: contract.payment_grace_days })]] : []),
              ...(totalMonths && contract.monthly_rent ? [
                [t('detail.kv.totalValue'), VND(contract.monthly_rent * totalMonths, lang)]
              ] : []),
            ].map(([k, v]) => (
              <div key={k} className="flex items-baseline justify-between gap-4">
                <dt className="text-[12px] text-stone-500 flex-shrink-0">{k}</dt>
                <dd className="text-[13px] font-medium text-stone-900 text-right">{v}</dd>
              </div>
            ))}
          </dl>
        </section>

        {/* Billing & Discounts — always visible, inline-editable */}
        <section className="py-4 border-b border-stone-100">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400">
              {t('detail.billing.title')}
            </p>
            {!editingBilling ? (
              <button
                onClick={startEditBilling}
                className="flex items-center gap-1 text-[11px] text-teal-600 hover:text-teal-700 font-medium"
              >
                <Icons.Edit size={12} /> {t('detail.billing.edit')}
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setEditingBilling(false)}
                  className="text-[11px] text-stone-500 hover:text-stone-700 font-medium"
                >
                  {t('detail.billing.cancel')}
                </button>
                <button
                  onClick={() => patchContract.mutate({
                    rent_waived: billingDraft.rent_waived,
                    first_month_discount: parseFloat(billingDraft.first_month_discount) || 0,
                    commission_amount: parseFloat(billingDraft.commission_amount) || 0,
                    commission_expense_category: billingDraft.commission_expense_category || null,
                  }, { onSuccess: () => toast.success('Đã lưu điều kiện thanh toán') })}
                  disabled={patchContract.isPending}
                  className="flex items-center gap-1 text-[11px] bg-teal-600 text-white px-2.5 py-1 rounded-md font-medium hover:bg-teal-700 disabled:opacity-50"
                >
                  <Icons.Check size={11} /> {patchContract.isPending ? t('detail.billing.saving') : t('detail.billing.save')}
                </button>
              </div>
            )}
          </div>

          {editingBilling ? (
            <div className="space-y-3">
              {/* Rent waived toggle */}
              <label className="flex items-start gap-3 p-3 rounded-lg bg-rose-50 border border-rose-100 cursor-pointer hover:bg-rose-100/60 transition-colors">
                <input
                  type="checkbox"
                  checked={billingDraft.rent_waived}
                  onChange={e => setBillingDraft(d => ({ ...d, rent_waived: e.target.checked }))}
                  className="mt-0.5 w-4 h-4 text-rose-600 rounded border-stone-300"
                />
                <div>
                  <p className="text-[12px] font-semibold text-rose-800">{t('detail.billing.rentWaivedLabel')}</p>
                  <p className="text-[11px] text-rose-500 mt-0.5">{t('detail.billing.rentWaivedDesc')}</p>
                </div>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-1">
                    {t('detail.billing.firstMonthDiscountLabel')}
                  </label>
                  <CurrencyInput
                    value={billingDraft.first_month_discount}
                    onChange={e => setBillingDraft(d => ({ ...d, first_month_discount: e.target.value }))}
                    disabled={billingDraft.rent_waived}
                    className="w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 disabled:bg-stone-100 disabled:text-stone-400"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-1">
                    {t('detail.billing.commissionLabel')}
                  </label>
                  <CurrencyInput
                    value={billingDraft.commission_amount}
                    onChange={e => setBillingDraft(d => ({ ...d, commission_amount: e.target.value }))}
                    className="w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
                  />
                </div>
              </div>
              {/* Commission expense category */}
              {parseFloat(billingDraft.commission_amount) > 0 && (
                <div>
                  <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-1">
                    {t('detail.billing.commissionTemplateLabel')}
                  </label>
                  <select
                    value={billingDraft.commission_expense_category ?? ''}
                    onChange={e => setBillingDraft(d => ({ ...d, commission_expense_category: e.target.value || null }))}
                    className="w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
                  >
                    <option value="">{t('detail.billing.commissionTemplateNone')}</option>
                    {expenseCategories.filter(c => c.is_active !== false).map(cat => (
                      <option key={cat.key} value={cat.key}>{cat.label}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-baseline justify-between gap-4">
                <dt className="text-[12px] text-stone-500 flex-shrink-0">{t('detail.billing.rentWaived')}</dt>
                <dd className="text-[13px] font-medium text-right">
                  {contract.rent_waived
                    ? <span className="text-rose-600 font-semibold">{t('detail.billing.rentWaivedYes')}</span>
                    : <span className="text-stone-400">{t('detail.billing.rentWaivedNo')}</span>}
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-4">
                <dt className="text-[12px] text-stone-500 flex-shrink-0">{t('detail.billing.firstMonthDiscount')}</dt>
                <dd className={`text-[13px] font-medium text-right ${(contract.first_month_discount > 0) ? 'text-rose-600' : 'text-stone-400'}`}>
                  {(contract.first_month_discount > 0) ? `−${VND(contract.first_month_discount, lang)}` : '—'}
                </dd>
              </div>
              <div className="flex items-start justify-between gap-4">
                <dt className="text-[12px] text-stone-500 flex-shrink-0 pt-0.5">{t('detail.billing.commission')}</dt>
                <dd className="flex flex-col items-end gap-1">
                  <span className={`text-[13px] font-medium ${(contract.commission_amount > 0) ? 'text-rose-600' : 'text-stone-400'}`}>
                    {(contract.commission_amount > 0) ? `−${VND(contract.commission_amount, lang)}` : '—'}
                  </span>
                  {contract.commission_amount > 0 && (
                    <button
                      onClick={() => syncCommission.mutate()}
                      disabled={syncCommission.isPending}
                      className="flex items-center gap-1 text-[11px] text-teal-600 hover:text-teal-700 font-medium disabled:opacity-50"
                    >
                      <Icons.Refresh className={`h-3 w-3 ${syncCommission.isPending ? 'animate-spin' : ''}`} />
                      {syncCommission.isPending ? 'Đang tạo…' : 'Tạo / cập nhật chi phí'}
                    </button>
                  )}
                </dd>
              </div>
              {contract.commission_amount > 0 && contract.commission_expense_category && (
                <div className="flex items-baseline justify-between gap-4">
                  <dt className="text-[12px] text-stone-500 flex-shrink-0">{t('detail.billing.commissionTemplate')}</dt>
                  <dd className="text-[13px] font-medium text-right text-stone-600">
                    {expenseCategories.find(c => c.key === contract.commission_expense_category)?.label
                      ?? contract.commission_expense_category}
                  </dd>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Services */}
        {(services.length > 0 || ['active', 'expiring_soon'].includes(contract.status)) && (
          <section className="py-4 border-b border-stone-100">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400">
                {t('detail.section.services', { count: services.filter(s => s.is_active !== false).length })}
              </p>
              {['active', 'expiring_soon'].includes(contract.status) && !editingServices && (
                <button
                  onClick={() => { setServicesDraft([...services]); setEditingServices(true); }}
                  className="flex items-center gap-1 text-[11px] text-teal-600 hover:text-teal-700 font-medium"
                >
                  <Icons.Edit size={11} /> {t('detail.billing.edit')}
                </button>
              )}
              {editingServices && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setEditingServices(false)}
                    className="text-[11px] text-stone-500 hover:text-stone-700 font-medium"
                  >
                    {t('detail.billing.cancel')}
                  </button>
                  <button
                    onClick={() => patchContract.mutate(
                      { services_config: servicesDraft },
                      { onSuccess: () => { setEditingServices(false); toast.success('Đã lưu dịch vụ — hóa đơn pending đang được tính lại'); } }
                    )}
                    disabled={patchContract.isPending}
                    className="flex items-center gap-1 text-[11px] bg-teal-600 text-white px-2 py-1 rounded font-medium hover:bg-teal-700 disabled:opacity-50"
                  >
                    <Icons.Check size={11} /> {patchContract.isPending ? t('detail.billing.saving') : t('detail.billing.save')}
                  </button>
                </div>
              )}
            </div>
            {editingServices ? (
              <ServiceConfigManager
                services={servicesDraft}
                onChange={setServicesDraft}
                title=""
              />
            ) : (
              <div className="space-y-1">
                {services.filter(s => s.is_active !== false).map((s, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 border-b border-stone-50 last:border-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-500 font-medium uppercase">
                        {s.type === 'metered' ? t('detail.service.metered') : t('detail.service.fixed')}
                      </span>
                      <span className="text-[13px] text-stone-800">{s.name || s.service_id}</span>
                    </div>
                    <span className="text-[13px] font-medium text-stone-900">
                      {s.type === 'metered'
                        ? t('detail.service.perUnit', { price: VND(s.unit_price, lang), unit: s.unit || t('detail.service.unit') })
                        : VND(s.unit_price, lang)
                      }
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Payment schedule */}
        {contract.start_date && contract.end_date && contract.monthly_rent && (
          <section className="py-4 border-b border-stone-100">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400 mb-3">{t('detail.section.schedule')}</p>
            <PaymentSchedule contract={contract} />
          </section>
        )}

        {/* Prepaid blocks — also on drafts: paying N months up front is agreed when the
            contract is signed, before it goes active. Blocks recorded on a draft apply
            as soon as activation generates the payments. */}
        {['draft', 'pending_signature', 'active', 'expiring_soon'].includes(contract.status) && (
          <section className="py-4 border-b border-stone-100">
            <PrepaymentManager contract={contract} />
          </section>
        )}

        {['active', 'expiring_soon'].includes(contract.status) && (
          <section className="py-4 border-b border-stone-100">
            <VacationManager contract={contract} />
          </section>
        )}

      </div>
    </div>
  );
}

// ── prepaid blocks (renter pays N months upfront) ─────────────────────────────
function PrepaymentManager({ contract }) {
  const qc = useQueryClient();
  const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  // Default to the contract's start month, or this month if it already started —
  // a first block almost always begins with the contract.
  const defaultMonth = () => {
    const now = ym(new Date());
    const start = contract?.start_date ? contract.start_date.slice(0, 7) : now;
    return start > now ? start : now;
  };
  const emptyForm = { start_month: '', months: 3, discount_amount: 0, notes: '' };
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const { data: prepayments = [] } = useQuery({
    queryKey: ['contract-prepayments', contract.id],
    queryFn: () => api.get(`/api/admin/contracts/${contract.id}/prepayments`).then(r => r.data?.prepayments ?? []),
    enabled: !!contract?.id,
    staleTime: 30 * 1000,
  });

  // Live preview of the amount, so the manager sees the total before recording it.
  const { data: quote } = useQuery({
    queryKey: ['prepayment-quote', contract.id, form.start_month, form.months, form.discount_amount],
    queryFn: () => api.get(`/api/admin/contracts/${contract.id}/prepayments/quote`, {
      params: { start_month: form.start_month, months: form.months, discount_amount: form.discount_amount || 0 },
    }).then(r => r.data),
    enabled: !!(showForm && contract?.id && form.start_month && form.months >= 1),
    staleTime: 10 * 1000,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['contract-prepayments', contract.id] });
    qc.invalidateQueries({ queryKey: ['payments-admin'] });
  };
  const closeForm = () => { setShowForm(false); setForm(emptyForm); };

  const save = useMutation({
    mutationFn: (body) => api.post(`/api/admin/contracts/${contract.id}/prepayments`, body).then(r => r.data),
    onSuccess: (d) => {
      invalidate();
      const n = d?.payments_recomputed ?? 0;
      // On a draft there are no payments yet — the block applies at activation.
      toast.success(n > 0
        ? `Đã ghi nhận trả trước (${n} kỳ được tính lại)`
        : 'Đã ghi nhận trả trước — sẽ áp dụng khi tạo kỳ thanh toán');
      closeForm();
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Lưu trả trước thất bại'),
  });
  const cancel = useMutation({
    mutationFn: (id) => api.delete(`/api/admin/prepayments/${id}`).then(r => r.data),
    onSuccess: () => { invalidate(); toast.success('Đã hủy kỳ trả trước'); },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Hủy thất bại'),
  });

  const openAdd = () => { setForm({ ...emptyForm, start_month: defaultMonth() }); setShowForm(true); };

  const valid = /^\d{4}-\d{2}$/.test(form.start_month) && form.months >= 1;
  const submit = () => {
    if (!valid || save.isPending) return;
    save.mutate({
      start_month: form.start_month,
      months: parseInt(form.months) || 1,
      discount_amount: parseFloat(form.discount_amount) || 0,
      notes: form.notes || null,
    });
  };

  const activeBlocks = prepayments.filter(p => p.status === 'active');
  const money = (n) => Number(n || 0).toLocaleString('vi-VN');
  const fmtMonth = (m) => m ? m.split('-').reverse().join('/') : '—';
  const fieldCls = 'w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400';
  const miniLabel = 'block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-1';

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400">💰 Trả trước nhiều tháng</p>
        {!showForm && (
          <button onClick={openAdd} className="text-[11px] text-teal-600 hover:text-teal-700 font-medium">+ Thêm kỳ trả trước</button>
        )}
      </div>

      {activeBlocks.length === 0 && !showForm && (
        <p className="text-[12px] text-stone-400 italic">Chưa có kỳ trả trước nào — đang tính tiền theo từng tháng.</p>
      )}

      <div className="space-y-2">
        {activeBlocks.map(p => (
          <div key={p.id} className="flex items-start justify-between gap-2 rounded-lg border border-teal-200 bg-teal-50/60 px-3 py-2">
            <div className="min-w-0">
              <div className="text-[12px] font-semibold text-teal-800">
                {p.months} tháng · {money(p.total_amount)} ₫
              </div>
              <div className="text-[11px] text-teal-700/80">
                {fmtMonth(p.start_month)} → {fmtMonth(p.end_month)}
                {p.discount_amount > 0 ? ` · ưu đãi ${money(p.discount_amount)} ₫` : ''}
                {p.notes ? ` · ${p.notes}` : ''}
              </div>
              <div className="text-[10px] text-teal-600/70 mt-0.5">Thu vào tháng {fmtMonth(p.start_month)}; các tháng còn lại chỉ tính điện nước.</div>
            </div>
            <button
              onClick={() => { if (window.confirm(`Hủy kỳ trả trước ${p.months} tháng? Các tháng sẽ quay lại tính tiền theo từng tháng.`)) cancel.mutate(p.id); }}
              disabled={cancel.isPending}
              className="text-[11px] text-rose-600 hover:underline disabled:opacity-40 flex-shrink-0">Hủy</button>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="mt-3 rounded-lg border border-stone-200 p-3 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div><label className={miniLabel}>Thu từ tháng</label>
              <input type="month" value={form.start_month} onChange={e => setForm(f => ({ ...f, start_month: e.target.value }))} className={fieldCls} /></div>
            <div><label className={miniLabel}>Số tháng</label>
              <input type="number" min={1} value={form.months} onChange={e => setForm(f => ({ ...f, months: Math.max(1, parseInt(e.target.value) || 1) }))} className={fieldCls} /></div>
          </div>
          <div><label className={miniLabel}>Ưu đãi trả trước (₫)</label>
            <input type="number" min={0} step={1000} value={form.discount_amount} onChange={e => setForm(f => ({ ...f, discount_amount: Math.max(0, parseFloat(e.target.value) || 0) }))} className={fieldCls} /></div>
          <div><label className={miniLabel}>Ghi chú</label>
            <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="VD: Đóng trước 5 tháng đầu" className={fieldCls} /></div>

          {quote && (
            <div className="rounded-lg bg-stone-50 border border-stone-200 px-3 py-2 text-[12px] text-stone-600 space-y-0.5">
              <div className="flex justify-between"><span>Tiền phòng + dịch vụ / tháng</span><span className="font-medium">{money(quote.monthly_total)} ₫</span></div>
              <div className="flex justify-between"><span>× {quote.months} tháng ({fmtMonth(quote.start_month)} → {fmtMonth(quote.end_month)})</span><span className="font-medium">{money(quote.months * quote.monthly_total)} ₫</span></div>
              {quote.discount_amount > 0 && (
                <div className="flex justify-between text-rose-600"><span>Ưu đãi</span><span className="font-medium">−{money(quote.discount_amount)} ₫</span></div>
              )}
              <div className="flex justify-between pt-1 border-t border-stone-200 text-teal-700 font-semibold"><span>Thu tháng {fmtMonth(quote.start_month)}</span><span>{money(quote.total_amount)} ₫</span></div>
              <p className="text-[10px] text-stone-400 pt-1">Điện nước vẫn tính riêng theo từng tháng.</p>
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <button onClick={closeForm} className="flex-1 h-9 rounded-lg border border-stone-200 text-[13px] font-medium text-stone-600 hover:bg-stone-50">Hủy</button>
            <button onClick={submit} disabled={!valid || save.isPending} className="flex-1 h-9 rounded-lg bg-teal-600 text-white text-[13px] font-medium hover:bg-teal-700 disabled:opacity-40">{save.isPending ? 'Đang lưu…' : 'Ghi nhận'}</button>
          </div>
        </div>
      )}
    </>
  );
}

// ── vacation manager (renter away, keeps the room) ────────────────────────────
function VacationManager({ contract }) {
  const qc = useQueryClient();
  const monthStart = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };
  const monthEnd = () => { const d = new Date(); const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(last).padStart(2, '0')}`; };
  const emptyForm = { scope: 'whole_room', start_date: '', end_date: '', away_count: 1, rent_discount_percent: 100, reason: '' };
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const { data: vacations = [] } = useQuery({
    queryKey: ['contract-vacations', contract.id],
    queryFn: () => api.get(`/api/admin/contracts/${contract.id}/vacations`).then(r => r.data?.vacations ?? []),
    enabled: !!contract?.id,
    staleTime: 30 * 1000,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['contract-vacations', contract.id] });
    qc.invalidateQueries({ queryKey: ['payments-admin'] });
  };
  const closeForm = () => { setShowForm(false); setEditingId(null); setForm(emptyForm); };

  const save = useMutation({
    mutationFn: (body) => editingId
      ? api.put(`/api/admin/vacations/${editingId}`, body).then(r => r.data)
      : api.post(`/api/admin/contracts/${contract.id}/vacations`, body).then(r => r.data),
    onSuccess: () => { invalidate(); toast.success(editingId ? 'Đã cập nhật kỳ nghỉ' : 'Đã ghi nhận kỳ nghỉ'); closeForm(); },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Lưu kỳ nghỉ thất bại'),
  });
  const cancel = useMutation({
    mutationFn: (id) => api.delete(`/api/admin/vacations/${id}`).then(r => r.data),
    onSuccess: () => { invalidate(); toast.success('Đã hủy kỳ nghỉ'); },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Hủy thất bại'),
  });

  const openAdd = () => { setEditingId(null); setForm({ ...emptyForm, start_date: monthStart(), end_date: monthEnd() }); setShowForm(true); };
  const openEdit = (v) => {
    setEditingId(v.id);
    setForm({ scope: v.scope, start_date: v.start_date, end_date: v.end_date, away_count: v.away_count || 1, rent_discount_percent: Number(v.rent_discount_percent || 0), reason: v.reason || '' });
    setShowForm(true);
  };

  const valid = form.start_date && form.end_date && form.end_date >= form.start_date &&
    form.rent_discount_percent >= 0 && form.rent_discount_percent <= 100 &&
    (form.scope === 'whole_room' || form.away_count >= 1);

  const submit = () => {
    if (!valid || save.isPending) return;
    save.mutate({
      scope: form.scope, start_date: form.start_date, end_date: form.end_date,
      rent_discount_percent: parseFloat(form.rent_discount_percent) || 0,
      away_count: form.scope === 'partial' ? (parseInt(form.away_count) || 1) : null,
      reason: form.reason || null,
    });
  };

  const activeVacs = vacations.filter(v => v.status === 'active');
  const fmt = (d) => d ? d.split('-').reverse().join('/') : '—';
  const fieldCls = 'w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400';
  const miniLabel = 'block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-1';

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400">🏖 Nghỉ (vắng nhưng giữ phòng)</p>
        {!showForm && (
          <button onClick={openAdd} className="text-[11px] text-amber-600 hover:text-amber-700 font-medium">+ Thêm kỳ nghỉ</button>
        )}
      </div>

      {activeVacs.length === 0 && !showForm && (
        <p className="text-[12px] text-stone-400 italic">Chưa ghi nhận kỳ nghỉ nào.</p>
      )}

      <div className="space-y-2">
        {activeVacs.map(v => (
          <div key={v.id} className="flex items-start justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2">
            <div className="min-w-0">
              <div className="text-[12px] font-semibold text-amber-800">
                {v.scope === 'whole_room' ? 'Cả phòng' : `Một số người (${v.away_count || 0})`} · −{Number(v.rent_discount_percent || 0)}% tiền phòng
              </div>
              <div className="text-[11px] text-amber-700/80">{fmt(v.start_date)} → {fmt(v.end_date)}{v.reason ? ` · ${v.reason}` : ''}</div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button onClick={() => openEdit(v)} className="text-[11px] text-teal-600 hover:underline">Sửa</button>
              <button onClick={() => { if (window.confirm('Hủy kỳ nghỉ này?')) cancel.mutate(v.id); }} disabled={cancel.isPending} className="text-[11px] text-rose-600 hover:underline disabled:opacity-40">Hủy</button>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="mt-3 rounded-lg border border-stone-200 p-3 space-y-3">
          <div>
            <label className={miniLabel}>Phạm vi</label>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setForm(f => ({ ...f, scope: 'whole_room' }))} className={`h-9 rounded-lg text-[13px] font-medium border transition-colors ${form.scope === 'whole_room' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-stone-600 border-stone-200'}`}>Cả phòng</button>
              <button type="button" onClick={() => setForm(f => ({ ...f, scope: 'partial' }))} className={`h-9 rounded-lg text-[13px] font-medium border transition-colors ${form.scope === 'partial' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-stone-600 border-stone-200'}`}>Một số người</button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className={miniLabel}>Từ ngày</label>
              <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} className={fieldCls} /></div>
            <div><label className={miniLabel}>Đến ngày</label>
              <input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} className={fieldCls} /></div>
          </div>
          {form.scope === 'partial' && (
            <div><label className={miniLabel}>Số người vắng</label>
              <input type="number" min={1} value={form.away_count} onChange={e => setForm(f => ({ ...f, away_count: Math.max(1, parseInt(e.target.value) || 1) }))} className={fieldCls} /></div>
          )}
          <div><label className={miniLabel}>% giảm tiền phòng</label>
            <input type="number" min={0} max={100} value={form.rent_discount_percent} onChange={e => setForm(f => ({ ...f, rent_discount_percent: Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)) }))} className={fieldCls} />
            <p className="text-[10px] text-stone-400 mt-1">Giảm = % × (số ngày vắng / số ngày trong tháng) × tiền phòng. Dịch vụ được miễn theo số ngày vắng.</p>
          </div>
          <div><label className={miniLabel}>Ghi chú</label>
            <input value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} placeholder="VD: Nghỉ hè về quê" className={fieldCls} /></div>
          <div className="flex gap-2 pt-1">
            <button onClick={closeForm} className="flex-1 h-9 rounded-lg border border-stone-200 text-[13px] font-medium text-stone-600 hover:bg-stone-50">Hủy</button>
            <button onClick={submit} disabled={!valid || save.isPending} className="flex-1 h-9 rounded-lg bg-amber-500 text-white text-[13px] font-medium hover:bg-amber-600 disabled:opacity-40">{save.isPending ? 'Đang lưu…' : (editingId ? 'Cập nhật' : 'Ghi nhận')}</button>
          </div>
        </div>
      )}
    </>
  );
}

// ── terminate modal ───────────────────────────────────────────────────────────
function MoveOutRenterModal({ contract, onClose, onConfirm, loading }) {
  // Only show active (not already moved-out) renters
  const tenants = (contract?.renters ?? []).filter(t => !t.moved_out_at);
  const [selectedId, setSelectedId] = useState(
    tenants.find(t => !t.is_primary)?.id ?? null
  );
  const [newPrimaryId, setNewPrimaryId] = useState(null);
  const [moveOutDate, setMoveOutDate] = useState(new Date().toISOString().slice(0, 10));

  if (!contract) return null;

  const selected = tenants.find(t => t.id === selectedId);
  const leavingPrimary = selected?.is_primary ?? false;
  // Remaining active renters after removal — candidates for new primary
  const remaining = tenants.filter(t => t.id !== selectedId);
  // When selection changes reset the new-primary pick
  const handleSelectLeaving = (id) => {
    setSelectedId(id);
    setNewPrimaryId(null);
  };

  const canConfirm = selectedId && (!leavingPrimary || newPrimaryId) && !loading;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-stone-900/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl border border-stone-200 shadow-2xl w-full max-w-sm mx-4 p-5 space-y-4">
        <h3 className="text-base font-semibold text-stone-900">Rời phòng</h3>
        <p className="text-[13px] text-stone-500">
          Chọn người thuê rời khỏi phòng <strong>{contract.room_number}</strong>.
          Hóa đơn pending sẽ được tính lại với số người thuê mới.
        </p>

        {/* Step 1 — who is leaving */}
        <div>
          <p className="text-[11px] font-semibold text-stone-400 uppercase tracking-wide mb-2">Người rời phòng</p>
          <div className="space-y-2">
            {tenants.map(tn => (
              <label
                key={tn.id}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedId === tn.id ? 'border-orange-300 bg-orange-50' : 'border-stone-200 hover:bg-stone-50'
                }`}
              >
                <input
                  type="radio"
                  name="moveout-renter"
                  value={tn.id}
                  checked={selectedId === tn.id}
                  onChange={() => handleSelectLeaving(tn.id)}
                  className="accent-orange-500"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-stone-900">
                    {tn.first_name} {tn.last_name}
                    {tn.is_primary && (
                      <span className="ml-2 text-[10px] bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded font-semibold">Chủ HĐ</span>
                    )}
                  </p>
                  {tn.email && <p className="text-[11px] text-stone-400">{tn.email}</p>}
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Move-out date — may be future; per-person services prorate to days stayed */}
        <div>
          <p className="text-[11px] font-semibold text-stone-400 uppercase tracking-wide mb-2">Ngày rời phòng</p>
          <input
            type="date"
            value={moveOutDate}
            onChange={e => setMoveOutDate(e.target.value)}
            className="w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-400"
          />
          <p className="text-[11px] text-stone-400 mt-1">
            Tiền phòng vẫn tính đủ (người ở lại chịu). Dịch vụ theo người của người rời được tính theo số ngày ở trong tháng rời phòng.
          </p>
        </div>

        {/* Step 2 — pick new contract holder when the primary is leaving */}
        {leavingPrimary && (
          <div>
            <p className="text-[11px] font-semibold text-stone-400 uppercase tracking-wide mb-2">
              Chủ hợp đồng mới <span className="text-red-400">*</span>
            </p>
            <p className="text-[12px] text-amber-700 bg-amber-50 px-3 py-2 rounded-lg mb-2">
              Người rời phòng là chủ hợp đồng. Chọn người sẽ tiếp nhận vai trò này.
            </p>
            <div className="space-y-2">
              {remaining.map(tn => (
                <label
                  key={tn.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    newPrimaryId === tn.id ? 'border-teal-300 bg-teal-50' : 'border-stone-200 hover:bg-stone-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="new-primary"
                    value={tn.id}
                    checked={newPrimaryId === tn.id}
                    onChange={() => setNewPrimaryId(tn.id)}
                    className="accent-teal-600"
                  />
                  <p className="text-[13px] font-medium text-stone-900">
                    {tn.first_name} {tn.last_name}
                  </p>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <button onClick={onClose}
            className="flex-1 h-9 rounded-lg border border-stone-200 text-[13px] font-medium text-stone-700 hover:bg-stone-50 transition-colors">
            Hủy
          </button>
          <button
            onClick={() => canConfirm && onConfirm(selectedId, leavingPrimary ? newPrimaryId : null, moveOutDate)}
            disabled={!canConfirm}
            className="flex-1 h-9 rounded-lg bg-orange-500 text-white text-[13px] font-medium hover:bg-orange-600 transition-colors disabled:opacity-50">
            {loading ? 'Đang xử lý…' : 'Xác nhận rời phòng'}
          </button>
        </div>
      </div>
    </div>
  );
}

function MoveInRenterModal({ contract, onClose, onConfirm, loading }) {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState(null);

  // All renters from the system
  const { data: allRenters = [] } = useQuery({
    queryKey: ['renters-list'],
    queryFn: () => api.get('/api/renters').then(r => {
      const d = r.data; return Array.isArray(d) ? d : (d?.renters ?? []);
    }),
    staleTime: 60_000,
  });

  // All active contracts to determine who's already occupied
  const { data: allContracts = [] } = useQuery({
    queryKey: ['contracts'],
    staleTime: 30_000,
  });

  if (!contract) return null;

  // Current active renter IDs on this contract (not moved out)
  const currentIds = new Set((contract.renters ?? []).filter(t => !t.moved_out_at).map(t => t.id));

  // Renters already on other active contracts
  const occupiedIds = new Set();
  allContracts.forEach(c => {
    if (c.id === contract.id) return;
    if (!['active', 'expiring_soon'].includes(c.status)) return;
    (c.renters ?? []).filter(r => !r.moved_out_at).forEach(r => occupiedIds.add(r.id));
  });

  const available = allRenters.filter(r => !currentIds.has(r.id) && !occupiedIds.has(r.id));

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-stone-900/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl border border-stone-200 shadow-2xl w-full max-w-sm mx-4 p-5 space-y-4">
        <h3 className="text-base font-semibold text-stone-900">Thêm người thuê</h3>
        <p className="text-[13px] text-stone-500">
          Chọn người thuê mới vào phòng <strong>{contract.room_number}</strong>.
          Hóa đơn pending sẽ được tính lại với số người thuê mới.
        </p>

        <div className="max-h-64 overflow-y-auto space-y-2">
          {available.length === 0 ? (
            <p className="text-[13px] text-stone-400 text-center py-6">
              Không có khách thuê khả dụng
            </p>
          ) : available.map(r => (
            <label
              key={r.id}
              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                selectedId === r.id ? 'border-teal-300 bg-teal-50' : 'border-stone-200 hover:bg-stone-50'
              }`}
            >
              <input
                type="radio"
                name="movein-renter"
                value={r.id}
                checked={selectedId === r.id}
                onChange={() => setSelectedId(r.id)}
                className="accent-teal-600"
              />
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium text-stone-900">
                  {r.first_name} {r.last_name}
                </p>
                {r.email && <p className="text-[11px] text-stone-400">{r.email}</p>}
              </div>
            </label>
          ))}
        </div>

        <div className="flex gap-2 pt-1">
          <button onClick={onClose}
            className="flex-1 h-9 rounded-lg border border-stone-200 text-[13px] font-medium text-stone-700 hover:bg-stone-50 transition-colors">
            Hủy
          </button>
          <button
            onClick={() => selectedId && onConfirm(selectedId)}
            disabled={!selectedId || loading}
            className="flex-1 h-9 rounded-lg bg-teal-600 text-white text-[13px] font-medium hover:bg-teal-700 transition-colors disabled:opacity-50">
            {loading ? 'Đang xử lý…' : 'Xác nhận vào phòng'}
          </button>
        </div>
      </div>
    </div>
  );
}

function TerminateModal({ contract, onClose, onConfirm, loading }) {
  const { t } = useTranslation(['contracts', 'common']);
  const [reason, setReason] = useState('');
  const todayStr = new Date().toISOString().split('T')[0];
  const [terminateDate, setTerminateDate] = useState(todayStr);

  if (!contract) return null;

  const contractNum = contract.contract_number || `#${contract.id}`;
  const endDate = contract.end_date ? new Date(contract.end_date) : null;
  const selectedDate = new Date(terminateDate);
  const isEarly = endDate && selectedDate < endDate;
  const daysEarly = isEarly
    ? Math.ceil((endDate - selectedDate) / (1000 * 60 * 60 * 24))
    : 0;

  // Min date = contract start; max = contract end (can't terminate after end)
  const minDate = contract.start_date ?? todayStr;
  const maxDate = contract.end_date ?? undefined;

  // Proration preview — live calc as admin picks the date
  const terminateDateObj = terminateDate ? new Date(terminateDate + 'T00:00:00') : null;
  const daysUsed = terminateDateObj ? terminateDateObj.getDate() : 0;
  const daysInMonth = terminateDateObj
    ? new Date(terminateDateObj.getFullYear(), terminateDateObj.getMonth() + 1, 0).getDate()
    : 30;
  const isPartialMonth = daysUsed > 0 && daysUsed < daysInMonth;
  const ratio = isPartialMonth ? daysUsed / daysInMonth : 1;
  const estimatedRent = isPartialMonth && contract.monthly_rent
    ? Math.round(parseFloat(contract.monthly_rent) * ratio)
    : null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-stone-900/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl border border-stone-200 shadow-2xl w-full max-w-sm mx-4 p-5 space-y-4">
        <h3 className="text-base font-semibold text-stone-900">{t('contracts:terminateModal.title')}</h3>
        <p className="text-[13px] text-stone-600">
          {t('contracts:terminateModal.contractLine', { number: contractNum, room: contract.room_number })}
        </p>

        {/* Termination date picker */}
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1.5">
            Ngày kết thúc thực tế <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={terminateDate}
            min={minDate}
            max={maxDate}
            onChange={e => setTerminateDate(e.target.value)}
            className="w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-400"
          />
        </div>

        {/* Early termination warning */}
        {isEarly && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2">
            <span className="text-amber-500 mt-0.5 flex-shrink-0">⚠</span>
            <div className="space-y-0.5">
              <p className="text-[12px] font-semibold text-amber-800">
                Kết thúc sớm — trước hạn {daysEarly} ngày
              </p>
              <p className="text-[11px] text-amber-700">
                Hóa đơn các tháng sau ngày kết thúc sẽ bị xóa.
              </p>
            </div>
          </div>
        )}

        {/* Proration preview — shown whenever termination is mid-month */}
        {isPartialMonth && (
          <div className="bg-sky-50 border border-sky-200 rounded-lg px-3 py-2.5 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-[12px] font-semibold text-sky-800">
                Tính tỷ lệ ngày
              </p>
              <span className="text-[12px] font-bold text-sky-700 tabular-nums">
                {daysUsed}/{daysInMonth} ngày ({Math.round(ratio * 100)}%)
              </span>
            </div>
            <div className="space-y-1 text-[11px] text-sky-700">
              {estimatedRent !== null && (
                <div className="flex justify-between">
                  <span>Tiền thuê ước tính</span>
                  <span className="font-medium tabular-nums">{VND(estimatedRent)}</span>
                </div>
              )}
              <div className="flex justify-between text-sky-600">
                <span>Dịch vụ cố định</span>
                <span>× {Math.round(ratio * 100)}%</span>
              </div>
              <div className="flex justify-between text-sky-600">
                <span>Điện / Nước</span>
                <span>tính thực tế (chưa có)</span>
              </div>
            </div>
            {/* Note: if pay_first rent was already collected, finalize only adjusts if still pending */}
            <p className="text-[10px] text-sky-500 border-t border-sky-100 pt-1.5">
              * Nếu tiền thuê tháng này đã thu (trả trước), hóa đơn cuối chỉ gồm đồng hồ.
            </p>
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1.5">{t('contracts:terminateModal.reasonLabel')} *</label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            rows={3}
            required
            placeholder={t('contracts:terminateModal.reasonPlaceholder')}
            className="w-full px-3 py-2 rounded-lg border border-stone-200 text-[13px] resize-none focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
          />
        </div>
        <div className="flex gap-2">
          <button onClick={onClose}
            className="flex-1 h-9 rounded-lg border border-stone-200 text-[13px] font-medium text-stone-700 hover:bg-stone-50 transition-colors">
            {t('common:action.cancel')}
          </button>
          <button
            onClick={() => reason.trim() && onConfirm(reason, terminateDate)}
            disabled={!reason.trim() || !terminateDate || loading}
            className="flex-1 h-9 rounded-lg bg-red-600 text-white text-[13px] font-medium hover:bg-red-700 transition-colors disabled:opacity-50">
            {loading ? t('contracts:terminateModal.processing') : t('contracts:terminateModal.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── edit contract drawer ──────────────────────────────────────────────────────
function EditContractDrawer({ contract, onClose, onSave, loading }) {
  const { t } = useTranslation('contracts');
  const fieldCls = 'w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] text-stone-900 bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400';
  const [form, setForm] = useState({
    start_date: contract.start_date ?? '',
    end_date: contract.end_date ?? '',
    monthly_rent: contract.monthly_rent ?? '',
    deposit_amount: contract.deposit_amount ?? '',
    payment_due_day: contract.payment_due_day ?? 5,
    rent_waived: contract.rent_waived ?? false,
    first_month_discount: contract.first_month_discount ?? 0,
    commission_amount: contract.commission_amount ?? 0,
  });

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      start_date: form.start_date || undefined,
      end_date: form.end_date || undefined,
      monthly_rent: parseFloat(form.monthly_rent) || undefined,
      deposit_amount: parseFloat(form.deposit_amount) || undefined,
      payment_due_day: parseInt(form.payment_due_day) || undefined,
      rent_waived: form.rent_waived,
      first_month_discount: parseFloat(form.first_month_discount) || 0,
      commission_amount: parseFloat(form.commission_amount) || 0,
    });
  };

  return (
    <Drawer open onClose={onClose} title={t('detail.editDrawer.title')} width={400}>
      <form onSubmit={handleSubmit} className="flex flex-col h-full">
        <DrawerBody>
          <div className="space-y-4 py-2">
            {/* Dates */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1.5">{t('detail.editDrawer.startDate')}</label>
                <input type="date" value={form.start_date} onChange={set('start_date')} className={fieldCls} />
              </div>
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1.5">{t('detail.editDrawer.endDate')}</label>
                <input type="date" value={form.end_date} onChange={set('end_date')} className={fieldCls} />
              </div>
            </div>
            {/* Rent & Deposit */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1.5">{t('detail.editDrawer.monthlyRent')}</label>
                <CurrencyInput value={form.monthly_rent} onChange={set('monthly_rent')} className={fieldCls} />
              </div>
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1.5">{t('detail.editDrawer.deposit')}</label>
                <CurrencyInput value={form.deposit_amount} onChange={set('deposit_amount')} className={fieldCls} />
              </div>
            </div>
            {/* Payment due day */}
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1.5">{t('detail.editDrawer.paymentDueDay')}</label>
              <input type="number" min="1" max="28" value={form.payment_due_day} onChange={set('payment_due_day')} className={fieldCls} />
            </div>
            {/* Billing overrides */}
            <div className="pt-1 border-t border-stone-100">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400 mb-3">{t('detail.billing.title')}</p>
              <label className="flex items-start gap-3 p-3 rounded-lg bg-rose-50 border border-rose-100 cursor-pointer hover:bg-rose-100/60 transition-colors mb-3">
                <input
                  type="checkbox"
                  checked={form.rent_waived}
                  onChange={e => setForm(f => ({ ...f, rent_waived: e.target.checked }))}
                  className="mt-0.5 w-4 h-4 text-rose-600 rounded border-stone-300"
                />
                <div>
                  <p className="text-[12px] font-semibold text-rose-800">{t('detail.billing.rentWaivedLabel')}</p>
                  <p className="text-[11px] text-rose-500 mt-0.5">{t('detail.billing.rentWaivedDesc')}</p>
                </div>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-1">{t('detail.billing.firstMonthDiscountLabel')}</label>
                  <CurrencyInput value={form.first_month_discount}
                    onChange={set('first_month_discount')} disabled={form.rent_waived}
                    className={`${fieldCls} disabled:bg-stone-100 disabled:text-stone-400`} />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-1">{t('detail.billing.commissionLabel')}</label>
                  <CurrencyInput value={form.commission_amount}
                    onChange={set('commission_amount')}
                    className={fieldCls} />
                </div>
              </div>
            </div>
          </div>
        </DrawerBody>
        <DrawerFooter>
          <button type="button" onClick={onClose}
            className="flex-1 h-9 rounded-lg border border-stone-200 text-[13px] font-medium text-stone-700 hover:bg-stone-50 transition-colors">
            {t('detail.editDrawer.cancel')}
          </button>
          <button type="submit" disabled={loading}
            className="flex-1 h-9 rounded-lg bg-teal-600 text-white text-[13px] font-medium hover:bg-teal-700 transition-colors disabled:opacity-50">
            {loading ? t('detail.editDrawer.saving') : t('detail.editDrawer.save')}
          </button>
        </DrawerFooter>
      </form>
    </Drawer>
  );
}

// ── delete modal ──────────────────────────────────────────────────────────────
function DeleteModal({ contract, onClose, onConfirm, loading }) {
  const { t } = useTranslation('contracts');
  const contractNum = contract.contract_number || t('contractNumberFallback', { id: contract.id });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/40 p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <Icons.Trash size={18} className="text-red-600" />
          </div>
          <h3 className="text-base font-semibold text-stone-900">{t('deleteModal.title')}</h3>
        </div>
        <p className="text-[13px] font-medium text-stone-700">
          {t('deleteModal.contractLine', { number: contractNum })}
        </p>
        <p className="text-[13px] text-stone-500">{t('deleteModal.body')}</p>
        <div className="flex gap-2 pt-1">
          <button type="button" onClick={onClose} disabled={loading}
            className="flex-1 h-9 rounded-lg border border-stone-200 text-[13px] font-medium text-stone-700 hover:bg-stone-50 transition-colors disabled:opacity-50">
            {t('deleteModal.cancel')}
          </button>
          <button type="button" onClick={onConfirm} disabled={loading}
            className="flex-1 h-9 rounded-lg bg-red-600 text-white text-[13px] font-medium hover:bg-red-700 transition-colors disabled:opacity-50">
            {loading ? t('deleteModal.processing') : t('deleteModal.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── renew modal ───────────────────────────────────────────────────────────────
function RenewModal({ contract, onClose, onConfirm, loading }) {
  const { t } = useTranslation('contracts');
  const [months, setMonths] = useState(12);

  const oldEnd = contract.end_date ? new Date(contract.end_date) : null;
  const newStart = oldEnd ? new Date(oldEnd.getTime() + 24 * 60 * 60 * 1000) : null;
  const newEnd = newStart ? new Date(newStart.getTime() + months * 30 * 24 * 60 * 60 * 1000) : null;
  const fmt = (d) => d ? d.toLocaleDateString('vi-VN') : '—';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/40 p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 space-y-4">
        <h3 className="text-base font-semibold text-stone-900">{t('renewModal.title')}</h3>
        <p className="text-[13px] text-stone-600">
          {t('renewModal.contractLine', {
            number: contract.contract_number || `#${contract.id}`,
            room: contract.room_number || '',
          })}
        </p>
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1.5">{t('renewModal.durationLabel')}</label>
          <div className="flex items-center gap-2">
            <input
              type="number" min={1} max={60} value={months}
              onChange={e => setMonths(Math.max(1, Math.min(60, parseInt(e.target.value, 10) || 1)))}
              className="w-24 h-9 px-3 rounded-lg border border-stone-200 text-[13px]"
            />
            <span className="text-[13px] text-stone-500">{t('renewModal.months')}</span>
            <div className="ml-auto flex items-center gap-1">
              {[6, 12, 24].map(m => (
                <button key={m} type="button" onClick={() => setMonths(m)}
                  className={`px-2.5 h-7 rounded text-[12px] font-medium transition-colors ${
                    months === m ? 'bg-teal-600 text-white' : 'border border-stone-200 text-stone-600 hover:bg-stone-50'
                  }`}>
                  {m}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="bg-teal-50 border border-teal-100 rounded-lg p-3 text-[12px] space-y-1">
          <div className="flex justify-between"><span className="text-stone-600">{t('renewModal.preview.oldEnd')}</span> <strong>{fmt(oldEnd)}</strong></div>
          <div className="flex justify-between"><span className="text-stone-600">{t('renewModal.preview.newStart')}</span> <strong>{fmt(newStart)}</strong></div>
          <div className="flex justify-between"><span className="text-stone-600">{t('renewModal.preview.newEnd')}</span> <strong className="text-teal-700">{fmt(newEnd)}</strong></div>
        </div>
        <p className="text-[11px] text-stone-500">{t('renewModal.note')}</p>
        <div className="flex items-center gap-2 justify-end">
          <button onClick={onClose} disabled={loading}
            className="px-3 h-9 rounded-lg border border-stone-200 text-[13px] font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50">
            {t('renewModal.cancel')}
          </button>
          <button onClick={() => onConfirm(months)} disabled={loading || months < 1}
            className="px-3 h-9 rounded-lg bg-teal-600 text-white text-[13px] font-medium hover:bg-teal-700 disabled:opacity-50">
            {loading ? t('renewModal.processing') : t('renewModal.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── create / edit template modal ──────────────────────────────────────────────
function CreateTemplateModal({ open, onClose, editingTemplate }) {
  const qc = useQueryClient();
  const [servicesConfig, setServicesConfig] = useState([]);

  useEffect(() => {
    if (open && editingTemplate) {
      if (Array.isArray(editingTemplate.services_config) && editingTemplate.services_config.length) {
        setServicesConfig(editingTemplate.services_config.map(s => ({ ...s })));
      } else {
        setServicesConfig([]);
      }
    } else if (!open) {
      setServicesConfig([]);
    }
  }, [open, editingTemplate]);

  const save = useMutation({
    mutationFn: (data) => editingTemplate
      ? contractService.updateTemplate(editingTemplate.id, data)
      : contractService.createTemplate(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contract-templates'] });
      toast.success(editingTemplate ? 'Template updated!' : 'Template created!');
      onClose();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || 'Failed to save template');
    },
  });

  if (!open) return null;

  const inputCls = 'w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400';

  const handleSubmit = (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    save.mutate({
      name: fd.get('name'),
      description: fd.get('description'),
      duration_months: parseInt(fd.get('duration_months')),
      deposit_months: parseInt(fd.get('deposit_months')),
      payment_due_days: parseInt(fd.get('payment_due_days')),
      payment_grace_days: parseInt(fd.get('payment_grace_days')) || 3,
      late_fee_percentage: parseFloat(fd.get('late_fee_percentage')),
      terms_and_conditions: fd.get('terms_and_conditions'),
      clauses: fd.get('clauses').split('\n').filter(c => c.trim()),
      utilities_included: servicesConfig.filter(s => s.is_included).map(s => s.name),
      services_config: servicesConfig.length > 0 ? servicesConfig : undefined,
      auto_renewal: fd.get('auto_renewal') === 'on',
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-3xl max-h-[90vh] flex flex-col rounded-xl shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 px-6 py-4 flex justify-between items-center flex-shrink-0">
          <h2 className="text-lg font-bold text-white">
            {editingTemplate ? `Edit Template: ${editingTemplate.name}` : 'Create Contract Template'}
          </h2>
          <button onClick={onClose} className="text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors">
            <Icons.X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Template Name *</label>
                <input name="name" required className={inputCls} defaultValue={editingTemplate?.name || ''} placeholder="e.g. Standard 12-Month" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Description</label>
                <input name="description" className={inputCls} defaultValue={editingTemplate?.description || ''} placeholder="Brief description" />
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Duration (months) *</label>
                <input type="number" name="duration_months" required min="1" max="60" className={inputCls}
                  defaultValue={editingTemplate?.duration_months || 12} />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Deposit (months) *</label>
                <input type="number" name="deposit_months" required min="0" max="12" className={inputCls}
                  defaultValue={editingTemplate?.deposit_months || 2} />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Payment Due (days) *</label>
                <input type="number" name="payment_due_days" required min="1" max="28" className={inputCls}
                  defaultValue={editingTemplate?.payment_due_days || 5} />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Grace Period (days)</label>
                <input type="number" name="payment_grace_days" min="0" max="15" className={inputCls}
                  defaultValue={editingTemplate?.payment_grace_days || 3} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Late Fee % *</label>
                <input type="number" name="late_fee_percentage" required min="0" max="100" step="0.1" className={inputCls}
                  defaultValue={editingTemplate?.late_fee_percentage || 5.0} />
              </div>
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" name="auto_renewal" className="w-4 h-4 text-teal-600 rounded"
                    defaultChecked={editingTemplate?.auto_renewal || false} />
                  <span className="text-[13px] text-stone-700">Auto-renewal enabled</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Terms & Conditions</label>
              <textarea name="terms_and_conditions" rows={3}
                className="w-full px-3 py-2 rounded-lg border border-stone-200 text-[13px] resize-none focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
                defaultValue={editingTemplate?.terms_and_conditions || ''}
                placeholder="Standard rental terms…" />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Clauses (one per line)</label>
              <textarea name="clauses" rows={3}
                className="w-full px-3 py-2 rounded-lg border border-stone-200 text-[13px] resize-none font-mono focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
                defaultValue={Array.isArray(editingTemplate?.clauses) ? editingTemplate.clauses.join('\n') : ''}
                placeholder="No smoking&#10;No pets&#10;Quiet hours after 10pm" />
            </div>

            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Services & Utility Pricing</p>
              <ServiceConfigManager
                services={servicesConfig}
                onChange={setServicesConfig}
                title="Template Services"
              />
            </div>
          </div>

          <div className="px-6 py-4 border-t border-stone-100 flex justify-end gap-3 bg-stone-50/50 flex-shrink-0">
            <button type="button" onClick={onClose}
              className="px-4 h-9 rounded-lg border border-stone-200 text-[13px] font-medium text-stone-700 hover:bg-stone-50 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={save.isPending}
              className="px-5 h-9 rounded-lg bg-indigo-600 text-white text-[13px] font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50">
              {save.isPending ? 'Saving…' : (editingTemplate ? 'Save Changes' : 'Create Template')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── upload word template modal ────────────────────────────────────────────────
function UploadTemplateModal({ open, onClose }) {
  const qc = useQueryClient();
  const [file, setFile] = useState(null);

  const upload = useMutation({
    mutationFn: ({ file: f, name, description }) => contractService.uploadTemplate(f, name, description),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contract-templates'] });
      toast.success('Word template uploaded!');
      setFile(null);
      onClose();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    },
  });

  if (!open) return null;

  const inputCls = 'w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400';

  const handleSubmit = (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    if (!file) return toast.error('Please select a file');
    upload.mutate({ file, name: fd.get('name'), description: fd.get('description') });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-xl shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4 flex justify-between items-center">
          <h2 className="text-base font-bold text-white">Upload Word Template</h2>
          <button onClick={onClose} className="text-white/80 hover:text-white p-1.5 rounded-full hover:bg-white/10 transition-colors">
            <Icons.X size={16} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-xs text-purple-700">
            Upload a <code>.docx</code> file with placeholders like <code>{`{renter_name}`}</code>, <code>{`{room_number}`}</code>, <code>{`{monthly_rent}`}</code> that get auto-filled when generating contracts.
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Template Name *</label>
            <input name="name" required className={inputCls} placeholder="e.g. Standard Lease Agreement" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Description</label>
            <input name="description" className={inputCls} placeholder="Optional description" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Word File (.docx) *</label>
            <input type="file" accept=".doc,.docx"
              onChange={e => setFile(e.target.files?.[0] || null)}
              className="w-full text-[13px] file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-purple-100 file:text-purple-700 file:font-medium file:text-xs hover:file:bg-purple-200 cursor-pointer" />
            {file && <p className="text-xs text-stone-500 mt-1">{file.name} ({(file.size / 1024).toFixed(0)} KB)</p>}
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 h-9 rounded-lg border border-stone-200 text-[13px] font-medium text-stone-700 hover:bg-stone-50 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={upload.isPending || !file}
              className="px-5 h-9 rounded-lg bg-purple-600 text-white text-[13px] font-medium hover:bg-purple-700 transition-colors disabled:opacity-50">
              {upload.isPending ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── templates panel ───────────────────────────────────────────────────────────
function TemplatesPanel() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  const { data: templates = [], isLoading, refetch } = useQuery({
    queryKey: ['contract-templates'],
    queryFn: () => contractService.getTemplates().then(d => Array.isArray(d) ? d : []),
    refetchOnMount: 'always',
    staleTime: 0,
  });

  const deleteTemplate = useMutation({
    mutationFn: (id) => contractService.deleteTemplate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contract-templates'] });
      toast.success('Template deleted');
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Delete failed'),
  });

  const downloadTemplate = useMutation({
    mutationFn: (id) => contractService.downloadTemplate(id),
    onSuccess: (data, id) => {
      const tmpl = templates.find(t => t.id === id);
      const filename = tmpl?.file_name || `template_${id}.docx`;
      const url = window.URL.createObjectURL(new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    },
    onError: () => toast.error('Download failed'),
  });

  const handleDelete = (tmpl) => {
    if (window.confirm(`Delete template "${tmpl.name}"? This cannot be undone.`)) {
      deleteTemplate.mutate(tmpl.id);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto bg-stone-50/30 p-6">
      {/* Actions */}
      <div className="flex flex-wrap gap-3 mb-6">
        <button onClick={() => { setEditingTemplate(null); setShowCreate(true); }}
          className="flex items-center gap-2 px-4 h-9 rounded-lg bg-indigo-600 text-white text-[13px] font-medium hover:bg-indigo-700 transition-colors">
          <Icons.Plus size={14} /> Create Template
        </button>
        <button onClick={() => setShowUpload(true)}
          className="flex items-center gap-2 px-4 h-9 rounded-lg border border-stone-200 bg-white text-stone-700 text-[13px] font-medium hover:bg-stone-50 transition-colors">
          <Icons.Upload size={14} /> Upload Word Template
        </button>
        <button onClick={() => refetch()}
          className="flex items-center gap-2 px-4 h-9 rounded-lg border border-stone-200 bg-white text-stone-700 text-[13px] font-medium hover:bg-stone-50 transition-colors">
          <Icons.Refresh size={14} /> Refresh
        </button>
      </div>

      {/* Info box */}
      <div className="mb-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
        <h3 className="text-sm font-semibold text-indigo-800 mb-2">Template Types</h3>
        <div className="grid grid-cols-2 gap-4 text-xs text-indigo-700">
          <div>
            <p className="font-medium">Form Templates (Create Template):</p>
            <p>Define contract terms, services, clauses. Used when generating contracts.</p>
          </div>
          <div>
            <p className="font-medium">Word Templates (Upload Word Template):</p>
            <p>Upload .docx files with placeholders like <code>{`{renter_name}`}</code> that get auto-filled.</p>
          </div>
        </div>
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="grid grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-stone-200 p-5 animate-pulse space-y-3">
              <div className="h-4 bg-stone-100 rounded w-3/4" />
              <div className="h-3 bg-stone-100 rounded w-full" />
              <div className="h-3 bg-stone-100 rounded w-2/3" />
            </div>
          ))}
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center py-16">
          <EmptyState
            icon={<Icons.FileCheck size={40} />}
            title="No templates yet"
            description="Create a form template or upload a Word template to get started"
          />
          <button onClick={() => { setEditingTemplate(null); setShowCreate(true); }}
            className="mt-4 px-4 h-9 rounded-lg border border-stone-200 text-[13px] font-medium text-stone-700 hover:bg-stone-50 transition-colors">
            Create First Template
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map(tmpl => (
            <div key={tmpl.id} className="bg-white rounded-xl border border-stone-200 p-5 hover:shadow-md transition-shadow flex flex-col">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="text-[14px] font-semibold text-stone-900">{tmpl.name}</h3>
                  {tmpl.file_path && (
                    <span className="inline-flex items-center gap-1 text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded mt-0.5 font-medium">
                      Word Template
                    </span>
                  )}
                </div>
                <Icons.Sparkles size={16} className="text-amber-400 flex-shrink-0" />
              </div>

              {tmpl.description && (
                <p className="text-[12px] text-stone-500 mb-3">{tmpl.description}</p>
              )}

              {tmpl.file_path ? (
                <div className="text-[12px] text-stone-600 space-y-1 mb-4 flex-1">
                  <p><span className="font-medium">File:</span> {tmpl.file_name || 'document.docx'}</p>
                  {tmpl.placeholders?.length > 0 && (
                    <div>
                      <span className="font-medium">Placeholders: </span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {tmpl.placeholders.slice(0, 5).map(ph => (
                          <code key={ph} className="px-1.5 py-0.5 bg-stone-100 text-stone-600 rounded text-[10px]">{`{${ph}}`}</code>
                        ))}
                        {tmpl.placeholders.length > 5 && (
                          <span className="text-[11px] text-stone-400">+{tmpl.placeholders.length - 5} more</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-[12px] text-stone-600 space-y-1 mb-4 flex-1">
                  <p><span className="font-medium">Duration:</span> {tmpl.duration_months} months</p>
                  <p><span className="font-medium">Deposit:</span> {tmpl.deposit_months} months</p>
                  <p><span className="font-medium">Payment due:</span> Day {tmpl.payment_due_days || 5} (+{tmpl.payment_grace_days || 3} grace)</p>
                  <p><span className="font-medium">Late fee:</span> {tmpl.late_fee_percentage}%</p>
                  {tmpl.utilities_included?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {tmpl.utilities_included.map(u => (
                        <span key={u} className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px]">{u}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-2 mt-auto">
                {tmpl.file_path ? (
                  <>
                    <button onClick={() => downloadTemplate.mutate(tmpl.id)}
                      disabled={downloadTemplate.isPending}
                      className="flex-1 flex items-center justify-center gap-1.5 h-8 rounded-lg border border-stone-200 text-[12px] font-medium text-stone-700 hover:bg-stone-50 transition-colors disabled:opacity-50">
                      <Icons.Download size={13} /> Download
                    </button>
                    <button onClick={() => handleDelete(tmpl)}
                      className="h-8 w-8 flex items-center justify-center rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition-colors">
                      <Icons.Trash size={13} />
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={() => { setEditingTemplate(tmpl); setShowCreate(true); }}
                      className="flex-1 flex items-center justify-center gap-1.5 h-8 rounded-lg border border-stone-200 text-[12px] font-medium text-stone-700 hover:bg-stone-50 transition-colors">
                      <Icons.Edit size={13} /> Edit
                    </button>
                    <button onClick={() => handleDelete(tmpl)}
                      className="h-8 w-8 flex items-center justify-center rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition-colors">
                      <Icons.Trash size={13} />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <CreateTemplateModal
        open={showCreate}
        onClose={() => { setShowCreate(false); setEditingTemplate(null); }}
        editingTemplate={editingTemplate}
      />
      <UploadTemplateModal open={showUpload} onClose={() => setShowUpload(false)} />
    </div>
  );
}

// ── tab definition ────────────────────────────────────────────────────────────
function useTabs() {
  const { t } = useTranslation('contracts');
  return [
    { key: 'all',            label: t('tab.all') },
    { key: 'active',         label: t('tab.active') },
    { key: 'expiring_soon',  label: t('tab.expiring_soon') },
    { key: 'terminated',     label: t('tab.terminated') },
  ];
}

// ── main component ─────────────────────────────────────────────────────────────
export default function ContractsEnhanced() {
  const qc = useQueryClient();
  const { t } = useTranslation('contracts');
  const TABS = useTabs();
  const [view, setView] = useState('contracts'); // 'contracts' | 'templates'
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [terminateTarget, setTerminateTarget] = useState(null);
  const [renewTarget, setRenewTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [moveOutTarget, setMoveOutTarget] = useState(null);
  const [moveInTarget, setMoveInTarget] = useState(null);

  // ── data ──────────────────────────────────────────────────────────────────────
  const { data: allContracts = [], isLoading, isError: contractsError, refetch: refetchContracts } = useQuery({
    queryKey: ['contracts'],
    queryFn: () => api.get('/api/contracts').then(r => {
      const d = r.data;
      return Array.isArray(d) ? d : (d?.contracts ?? []);
    }),
    staleTime: 2 * 60 * 1000,
  });

  // Derive the live selected contract from the query data by ID
  const selected = useMemo(() =>
    allContracts.find(c => c.id === selectedId) ?? null,
    [allContracts, selectedId]
  );

  const terminate = useMutation({
    mutationFn: ({ id, reason, terminateDate }) => {
      const params = new URLSearchParams({ ...(reason && { reason }), ...(terminateDate && { terminate_date: terminateDate }) });
      return api.put(`/api/contracts/${id}/terminate?${params}`).then(r => r.data);
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['contracts'] });
      qc.invalidateQueries({ queryKey: ['payments'] });
      setTerminateTarget(null);
      if (data.status === 'expiring_soon') {
        toast.success(`Đã lên lịch kết thúc hợp đồng vào ngày ${data.terminate_date} — hợp đồng chuyển sang Sắp hết hạn`);
      } else {
        toast.success(`Đã kết thúc hợp đồng${data.terminate_date ? ` — ngày ${data.terminate_date}` : ''}`);
        if (data.needs_meter_reading) {
          setTimeout(() => toast(
            '⚡ Nhớ nhập chỉ số điện/nước cuối kỳ cho phòng này trong Chi số đồng hồ',
            { duration: 6000, icon: '📋' }
          ), 800);
        }
      }
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Kết thúc thất bại'),
  });

  const activate = useMutation({
    mutationFn: (id) => api.put(`/api/contracts/${id}/sign`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contracts'] });
      toast.success('Hợp đồng đã được kích hoạt');
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Kích hoạt thất bại'),
  });

  const revertToDraft = useMutation({
    mutationFn: (id) => api.put(`/api/contracts/${id}/revert-to-draft`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contracts'] });
      toast.success('Đã hoàn tác về trạng thái nháp');
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Hoàn tác thất bại'),
  });

  const recover = useMutation({
    mutationFn: (id) => api.put(`/api/contracts/${id}/reactivate`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contracts'] });
      toast.success('Đã khôi phục hợp đồng về trạng thái hoạt động');
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Khôi phục thất bại'),
  });

  const editContract = useMutation({
    mutationFn: ({ id, ...data }) => api.put(`/api/contracts/${id}`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contracts'] });
      setEditTarget(null);
      toast.success(t('editSuccess'));
    },
    onError: (err) => toast.error(err?.response?.data?.detail || t('editError')),
  });

  const deleteContract = useMutation({
    mutationFn: (id) => api.delete(`/api/contracts/${id}`).then(r => r.data),
    onSuccess: (_data, deletedId) => {
      qc.invalidateQueries({ queryKey: ['contracts'] });
      if (selectedId === deletedId) setSelectedId(null);
      setDeleteTarget(null);
    },
  });

  const renew = useMutation({
    mutationFn: ({ id, months }) =>
      api.post(`/api/contracts/${id}/renew?renewal_months=${months}`, {}).then(r => r.data),
    onSuccess: (newContract) => {
      qc.invalidateQueries({ queryKey: ['contracts'] });
      setRenewTarget(null);
      if (newContract?.id) setSelectedId(newContract.id);
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail || err?.message || 'Renewal failed';
      console.error('Contract renewal failed:', err?.response?.status, detail, err);
    },
  });

  const moveInRenter = useMutation({
    mutationFn: ({ contractId, renterId }) =>
      api.post(`/api/contracts/${contractId}/tenants/${renterId}/move-in`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contracts'] });
      setMoveInTarget(null);
      toast.success('Đã thêm người thuê. Hóa đơn pending đang được tính lại.');
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Thêm người thuê thất bại'),
  });

  const moveOutRenter = useMutation({
    mutationFn: ({ contractId, renterId, newPrimaryId, moveOutDate }) => {
      const qp = new URLSearchParams();
      if (newPrimaryId) qp.set('new_primary_id', newPrimaryId);
      if (moveOutDate) qp.set('move_out_date', moveOutDate);
      const params = qp.toString() ? `?${qp.toString()}` : '';
      return api.post(`/api/contracts/${contractId}/tenants/${renterId}/move-out${params}`).then(r => r.data);
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['contracts'] });
      setMoveOutTarget(null);
      toast.success(
        data.was_primary
          ? 'Đã xóa người thuê chính — người kế tiếp được đặt làm chủ HĐ. Hóa đơn đang tính lại.'
          : 'Đã xóa người thuê. Hóa đơn pending đang được tính lại.'
      );
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Rời phòng thất bại'),
  });

  // ── computed ──────────────────────────────────────────────────────────────────
  const kpi = useMemo(() => {
    const active    = allContracts.filter(c => c.status === 'active').length;
    const expiring  = allContracts.filter(c => {
      const d = daysUntil(c.end_date);
      return c.status === 'active' && d !== null && d <= 30 && d >= 0;
    }).length;
    const pending   = allContracts.filter(c => c.status === 'pending' || c.status === 'pending_signature').length;
    return { total: allContracts.length, active, expiring, pending };
  }, [allContracts]);

  const tabCounts = useMemo(() => ({
    all:           allContracts.length,
    active:        allContracts.filter(c => ['active', 'expiring_soon'].includes(c.status)).length,
    expiring_soon: allContracts.filter(c => {
      const d = daysUntil(c.end_date);
      return c.status === 'expiring_soon' || (c.status === 'active' && d !== null && d <= 30 && d >= 0);
    }).length,
    terminated:    allContracts.filter(c => c.status === 'terminated').length,
  }), [allContracts]);

  const filteredContracts = useMemo(() => {
    let list = allContracts;
    if (tab === 'active')        list = list.filter(c => ['active', 'expiring_soon'].includes(c.status));
    if (tab === 'expiring_soon') list = list.filter(c => {
      const d = daysUntil(c.end_date);
      return c.status === 'expiring_soon' || (c.status === 'active' && d !== null && d <= 30 && d >= 0);
    });
    if (tab === 'terminated')    list = list.filter(c => c.status === 'terminated');
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(c =>
        c.contract_number?.toLowerCase().includes(q) ||
        c.renter_name?.toLowerCase().includes(q) ||
        c.room_number?.toLowerCase().includes(q) ||
        c.building_name?.toLowerCase().includes(q) ||
        c.renters?.some(r => `${r.first_name} ${r.last_name}`.toLowerCase().includes(q))
      );
    }
    return list;
  }, [allContracts, tab, search]);

  // Group filtered contracts: building → room → contracts[]
  const groupedContracts = useMemo(() => {
    const bMap = {};
    filteredContracts.forEach(c => {
      const bKey = c.building_id ?? 'none';
      const bName = c.building_name || 'Tòa nhà khác';
      const rKey = c.room_id ?? 'none';
      const rNum = c.room_number || '—';
      if (!bMap[bKey]) bMap[bKey] = { id: bKey, name: bName, rooms: {} };
      if (!bMap[bKey].rooms[rKey]) bMap[bKey].rooms[rKey] = { id: rKey, number: rNum, contracts: [] };
      bMap[bKey].rooms[rKey].contracts.push(c);
    });
    return Object.values(bMap)
      .sort((a, b) => String(a.name).localeCompare(String(b.name)))
      .map(b => ({
        ...b,
        rooms: Object.values(b.rooms).sort((a, b) => String(a.number).localeCompare(String(b.number)))
      }));
  }, [filteredContracts]);

  const [collapsedBuildings, setCollapsedBuildings] = useState(new Set());
  const toggleBuilding = (bId) => setCollapsedBuildings(prev => {
    const next = new Set(prev);
    next.has(bId) ? next.delete(bId) : next.add(bId);
    return next;
  });

  if (contractsError) {
    return <QueryErrorBanner onRetry={refetchContracts} className="mt-4" />;
  }

  return (
    <div className="flex flex-col -mx-4 sm:-mx-6 lg:-mx-8 h-[calc(100vh-52px)] overflow-hidden">

      {/* ── Page-level nav: Contracts | Templates ── */}
      <div className="flex items-center gap-1 px-4 py-2 border-b border-stone-200 bg-white flex-shrink-0">
        <button
          onClick={() => setView('contracts')}
          className={`flex items-center gap-1.5 px-3 h-8 rounded-lg text-[13px] font-medium transition-colors ${
            view === 'contracts' ? 'bg-teal-600 text-white' : 'text-stone-600 hover:bg-stone-100'
          }`}
        >
          <Icons.FileCheck size={14} />
          Contracts
          {kpi.total > 0 && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
              view === 'contracts' ? 'bg-white/20 text-white' : 'bg-stone-100 text-stone-500'
            }`}>
              {kpi.total}
            </span>
          )}
        </button>
        <button
          onClick={() => setView('templates')}
          className={`flex items-center gap-1.5 px-3 h-8 rounded-lg text-[13px] font-medium transition-colors ${
            view === 'templates' ? 'bg-indigo-600 text-white' : 'text-stone-600 hover:bg-stone-100'
          }`}
        >
          <Icons.Sparkles size={14} />
          Templates
        </button>
      </div>

      {/* ── Contracts view ── */}
      {view === 'contracts' && (
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Left: list panel — full-width on mobile when no contract selected, hidden when one is */}
          <div className={`flex-shrink-0 border-r border-stone-200 bg-white flex flex-col
            ${selectedId ? 'hidden md:flex md:w-[320px]' : 'flex w-full md:w-[320px]'}`}>
            {/* Header */}
            <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between gap-2">
              <h1 className="text-[14px] font-semibold text-stone-900">{t('title')}</h1>
              <button
                onClick={() => setShowCreate(true)}
                className="flex items-center gap-1 px-2.5 h-8 rounded-lg bg-teal-600 text-white text-[12px] font-medium hover:bg-teal-700 transition-colors flex-shrink-0"
              >
                <Icons.Plus size={13} />
                {t('create.action')}
              </button>
            </div>

            {/* Search */}
            <div className="px-3 py-2 border-b border-stone-100">
              <div className="relative">
                <Icons.Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder={t('search.placeholder')}
                  className="w-full h-8 pl-7 pr-3 rounded-lg border border-stone-200 text-[12px] placeholder:text-stone-400 bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
                />
              </div>
            </div>

            {/* Tabs */}
            <div className="flex items-center overflow-x-auto border-b border-stone-100 px-1">
              {TABS.map(tb => (
                <button
                  key={tb.key}
                  onClick={() => setTab(tb.key)}
                  className={`flex items-center gap-1 px-2.5 py-2 text-[12px] font-medium border-b-2 whitespace-nowrap transition-colors -mb-px ${
                    tab === tb.key ? 'border-teal-600 text-teal-700' : 'border-transparent text-stone-500 hover:text-stone-700'
                  }`}
                >
                  {tb.label}
                  {tabCounts[tb.key] > 0 && (
                    <span className={`text-[10px] px-1 py-0.5 rounded-full font-semibold ${
                      tab === tb.key ? 'bg-teal-100 text-teal-700' : 'bg-stone-100 text-stone-400'
                    }`}>
                      {tabCounts[tb.key]}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Contract list — grouped by building → room */}
            <div className="flex-1 overflow-y-auto">
              {isLoading ? (
                <div className="divide-y divide-stone-100">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="px-4 py-3 space-y-1.5">
                      <div className="h-3.5 bg-stone-100 rounded animate-pulse w-3/4" />
                      <div className="h-3 bg-stone-100 rounded animate-pulse w-1/2" />
                    </div>
                  ))}
                </div>
              ) : filteredContracts.length === 0 ? (
                <EmptyState compact icon={<Icons.FileCheck />} title={t('empty.title')} />
              ) : (
                groupedContracts.map(building => {
                  const collapsed = collapsedBuildings.has(building.id);
                  const activeCount = building.rooms
                    .flatMap(r => r.contracts)
                    .filter(c => c.status === 'active' || c.status === 'expiring_soon').length;
                  return (
                    <div key={building.id}>
                      {/* Building header */}
                      <button
                        onClick={() => toggleBuilding(building.id)}
                        className="w-full flex items-center justify-between px-4 py-2 bg-stone-100 border-b border-stone-200 hover:bg-stone-200/60 transition-colors sticky top-0 z-10"
                      >
                        <div className="flex items-center gap-2">
                          <Icons.Building size={12} className="text-stone-500 flex-shrink-0" />
                          <span className="text-[11px] font-bold text-stone-600 uppercase tracking-wide truncate">
                            {building.name}
                          </span>
                          {activeCount > 0 && (
                            <span className="text-[10px] bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded-full font-semibold">
                              {activeCount}
                            </span>
                          )}
                        </div>
                        <Icons.ChevronDown size={12} className={`text-stone-400 flex-shrink-0 transition-transform ${collapsed ? '-rotate-90' : ''}`} />
                      </button>

                      {!collapsed && building.rooms.map(room => (
                        <div key={room.id}>
                          {/* Room header */}
                          <div className="flex items-center gap-2 px-4 py-1.5 bg-stone-50 border-b border-stone-100">
                            <Icons.Home size={11} className="text-stone-400 flex-shrink-0" />
                            <span className="text-[11px] font-semibold text-stone-500">
                              Phòng {room.number}
                            </span>
                            <span className="text-[10px] text-stone-400">
                              · {room.contracts.length} hợp đồng
                            </span>
                          </div>
                          {/* Contracts under this room */}
                          {room.contracts.map(c => (
                            <ContractItem
                              key={c.id}
                              contract={c}
                              selected={selectedId === c.id}
                              onClick={() => setSelectedId(c.id)}
                              grouped
                            />
                          ))}
                        </div>
                      ))}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Right: detail panel — hidden on mobile when no contract selected */}
          <div className={`flex-1 min-w-0 bg-white flex flex-col overflow-hidden ${!selectedId ? 'hidden md:flex' : 'flex'}`}>
            {/* Mobile back button */}
            {selectedId && (
              <button
                className="md:hidden w-full flex items-center gap-2 px-4 py-3 border-b border-stone-100 bg-stone-50 text-teal-700 text-[13px] font-medium hover:bg-stone-100 transition-colors flex-shrink-0"
                onClick={() => setSelectedId(null)}
              >
                <Icons.ChevronLeft size={16} />
                {t('title')}
              </button>
            )}

            {/* Global KPI strip */}
            <div className="px-4 sm:px-6 py-3 border-b border-stone-100 bg-stone-50/50 flex-shrink-0">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                <KPI label={t('kpi.total')}    value={String(kpi.total)}    icon={<Icons.FileCheck />} accent="neutral" />
                <KPI label={t('kpi.active')}   value={String(kpi.active)}   icon={<Icons.CheckCircle />} accent="success" />
                <KPI label={t('kpi.expiring')} value={String(kpi.expiring)} icon={<Icons.Clock />} accent={kpi.expiring > 0 ? 'warning' : 'neutral'} />
                <KPI label={t('kpi.pending')}  value={String(kpi.pending)}  icon={<Icons.Alert />} accent={kpi.pending > 0 ? 'warning' : 'neutral'} />
              </div>
            </div>

            <ContractDetail
              contract={selected}
              onTerminate={(c) => setTerminateTarget(c)}
              onRenew={(c) => setRenewTarget(c)}
              onDelete={(c) => setDeleteTarget(c)}
              onEdit={(c) => setEditTarget(c)}
              onActivate={(c) => activate.mutate(c.id)}
              onRevertToDraft={(c) => revertToDraft.mutate(c.id)}
              onMoveIn={(c) => setMoveInTarget(c)}
              onMoveOut={(c) => setMoveOutTarget(c)}
              onRecover={(c) => recover.mutate(c.id)}
              activating={activate.isPending}
              reverting={revertToDraft.isPending}
            />
          </div>
        </div>
      )}

      {/* ── Templates view ── */}
      {view === 'templates' && <TemplatesPanel />}

      {/* ── Create contract modal ── */}
      <CreateContractModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
      />

      {/* ── Edit contract drawer ── */}
      {editTarget && (
        <EditContractDrawer
          contract={editTarget}
          onClose={() => setEditTarget(null)}
          onSave={(data) => editContract.mutate({ id: editTarget.id, ...data })}
          loading={editContract.isPending}
        />
      )}

      {/* ── Move-in renter modal ── */}
      {moveInTarget && (
        <MoveInRenterModal
          contract={moveInTarget}
          onClose={() => setMoveInTarget(null)}
          onConfirm={(renterId) => moveInRenter.mutate({ contractId: moveInTarget.id, renterId })}
          loading={moveInRenter.isPending}
        />
      )}

      {/* ── Move-out renter modal ── */}
      {moveOutTarget && (
        <MoveOutRenterModal
          contract={moveOutTarget}
          onClose={() => setMoveOutTarget(null)}
          onConfirm={(renterId, newPrimaryId, moveOutDate) => moveOutRenter.mutate({ contractId: moveOutTarget.id, renterId, newPrimaryId, moveOutDate })}
          loading={moveOutRenter.isPending}
        />
      )}

      {/* ── Terminate modal ── */}
      {terminateTarget && (
        <TerminateModal
          contract={terminateTarget}
          onClose={() => setTerminateTarget(null)}
          onConfirm={(reason, terminateDate) => terminate.mutate({ id: terminateTarget.id, reason, terminateDate })}
          loading={terminate.isPending}
        />
      )}

      {/* ── Delete modal ── */}
      {deleteTarget && (
        <DeleteModal
          contract={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => deleteContract.mutate(deleteTarget.id)}
          loading={deleteContract.isPending}
        />
      )}

      {/* ── Renew modal ── */}
      {renewTarget && (
        <RenewModal
          contract={renewTarget}
          onClose={() => setRenewTarget(null)}
          onConfirm={(months) => renew.mutate({ id: renewTarget.id, months })}
          loading={renew.isPending}
        />
      )}
    </div>
  );
}
